
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Upload, ArrowRight, X, SquarePen, Play, ChartSpline, ImageDown, Image, ImagePlay, Text, FileText, SquareChevronRight, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, ListPlus, Globe, Database, BrainCircuit, Hash, Calendar, Brain, Home, BarChart } from 'lucide-react';
import { visualQaExperimentalApiHost, visualQaDataUploadApiHost } from './config/apiConfig';
import VisualQaHandler from "./helpers/visualQaHandler";
import { getStage, getRegion, suggestedQueries, formatDate } from './helpers/appUtils.js';
import ReactMarkdown from "react-markdown";
import { getDiscoverImagesV2RequestBody, getDiscoverImagesV2RequestWithGroundingBody, getDiscoverVideosRequestBody, getDiscoverVideosRequestWithGroundingBody, VIDEO_PROFILES, DEFAULT_VIDEO_PROFILE } from './helpers/exampleDiscoverImagesV2RequestBody';
import { initializeAmplify } from './auth/intitalizeCognito'
import { Auth, Hub } from "aws-amplify";
import AnnotationUploader from './helpers/annotationUploader';
import ImageUploader from './helpers/imageUploader';

const IMAGE_FALLBACK = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="600" height="400" viewBox="0 0 600 400"%3E%3Crect width="600" height="400" fill="%23e5e7eb"/%3E%3Ctext x="300" y="205" text-anchor="middle" font-family="sans-serif" font-size="24" fill="%234b5563"%3EImage unavailable%3C/text%3E%3C/svg%3E';

const TextWithEntities = ({text, getEntityInfo, wordBoundaries, setWordBoundaries, scrollToEntity}) => {
  useEffect(() => {
    if (text && getEntityInfo) {
      getEntityInfo(text).then(setWordBoundaries).catch(() => setWordBoundaries([]));
    }
  }, [text, getEntityInfo]);

  return <HighlightEntitiesInText text={text} wordBoundaries={wordBoundaries} scrollToEntity={scrollToEntity} />;
};

const HighlightEntitiesInText = ({text, wordBoundaries, scrollToEntity}) => {
  const createSegments = () => {
    if (!wordBoundaries || wordBoundaries.length === 0) {
      return [{ text, isWord: false }];
    }

    const segments = [];
    let lastEnd = 0;

    const filteredBoundaries = wordBoundaries.filter( wb => wb.info && wb.info !== '');
    const sortedBoundaries = [...filteredBoundaries].sort((a, b) => a.start - b.start);

    sortedBoundaries.forEach(({ start, end, info }, index) => {
      if (start > lastEnd) {
        segments.push({
          text: text.slice(lastEnd, start),
          isWord: false
        });
      }

      if (info && info !== '') {
        segments.push({
          text: text.slice(start, end),
          isWord: true,
          info: info,
          entityIndex: index
        });
      }

      lastEnd = end;
    });

    if (lastEnd < text.length) {
      segments.push({
        text: text.slice(lastEnd),
        isWord: false
      });
    }

    return segments;
  };

  const segments = createSegments();

  return (
    <div>
      <div className="text-base leading-relaxed">
        {segments.map((segment, index) => (
          <span key={index}>
            {segment.isWord ? (
              <span
                className="underline decoration-2 decoration-blue-500 cursor-pointer hover:decoration-blue-700 hover:bg-blue-50 transition-all duration-200 px-1 py-0.5 rounded"
                onClick={() => scrollToEntity(segment.entityIndex)}
                title="Click to view entity details below"
              >
                {segment.text}
              </span>
            ) : (
              segment.text
            )}
          </span>
        ))}
      </div>
    </div>
  );
};

const EditableTextToggleDisplay = ({ answer, handleLLMAnswerChange, subqueries, isDarkMode }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [text, setText] = useState(answer);

  const toggleVisibility = () => {
    setIsVisible(!isVisible);
  };

  const handleDoubleClick = () => {
    setIsEditing(true);
  };

  const handleSave = () => {
    setIsEditing(false);
    handleLLMAnswerChange(text);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleTextChange = (e) => {
    setText(e.target.value);
  };

  // Calculate textarea height based on text content
  const calculateTextareaHeight = (text) => {
    const lines = text.split('\n').length;
    const minHeight = Math.max(lines * 24 + 16, 60); // 24px per line + 16px padding
    return `${minHeight}px`;
  };

  return (
    <div className="max-w-4xl m-4">
      <div className="mb-6">
        <button
          onClick={toggleVisibility}
          className={`px-3 py-1 text-xs rounded-md ${isDarkMode ? 'bg-gradient-to-r from-sky-300 to-sky-200 text-[#242424]' : 'bg-gradient-to-r from-sky-900 to-sky-700 text-[#fafafa]'}`}
        >
          {isVisible ? (
            <>
              <ChevronUp size={16} className="inline mr-1" />
              Hide Grounded Answer
            </>
          ) : (
            <>
              <ChevronDown size={16} className="inline mr-1" />
              Show Grounded Answer
            </>
          )}
        </button>
      </div>

      {isVisible && (
        <>
          <div className="prose prose-gray max-w-none">
            {isEditing ? (
              <>
                <textarea
                  value={text}
                  onChange={handleTextChange}
                  style={{ height: calculateTextareaHeight(text) }}
                  className={`w-full p-2 border rounded-lg resize-none focus:outline-none focus:ring-2 leading-relaxed ${isDarkMode ? 'bg-[#404040] border-[#555555] text-[#fafafa] focus:border-sky-300 focus:ring-sky-300' : 'bg-white border-gray-300 text-gray-700 focus:ring-blue-500 focus:border-transparent'}`}
                  autoFocus
                />
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={handleSave}
                    className={`px-3 py-1 text-xs rounded-md ${isDarkMode ? 'bg-gradient-to-r from-sky-300 to-sky-200 text-[#242424]' : 'bg-gradient-to-r from-sky-900 to-sky-700 text-[#fafafa]'}`}
                  >
                    <SquarePen size={14} className="inline mr-1" />
                    Save
                  </button>
                  <button
                    onClick={handleCancel}
                    className={`px-3 py-1 text-xs rounded-md ${isDarkMode ? 'bg-gradient-to-r from-gray-600 to-gray-500 text-white hover:from-gray-700 hover:to-gray-600' : 'bg-gradient-to-r from-gray-600 to-gray-500 text-white hover:from-gray-700 hover:to-gray-600'}`}
                  >
                    <X size={14} className="inline mr-1" />
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <div
                className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'} leading-relaxed space-y-4 cursor-pointer p-2 rounded transition-colors`}
                onDoubleClick={handleDoubleClick}
                title="Double-click to edit"
              >
                {text.split('\n\n').map((paragraph, index) => (
                  <p key={index} className="text-base">
                    <ReactMarkdown>{paragraph}</ReactMarkdown>
                  </p>
                ))}
                
                {subqueries && subqueries.length > 0 && (
                  <div className={`mt-4 pt-4 border-t ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                    <h4 className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mb-2`}>Subqueries used:</h4>
                    <ul className="list-disc list-inside space-y-1">
                      {subqueries.map((subquery, index) => (
                        <li key={index} className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          {subquery}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          {!isEditing && (
            <div className={`mt-6 pt-4 border-t ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
              <div className="flex items-center justify-between text-sm text-gray-500">
                <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Double-click to edit</span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// VisualQA Logo Component
const VisualQALogo = ({ isDarkMode }) => (
  <div className="flex items-center mr-4 self-center">
    <span className={`font-light text-3xl text-transparent bg-clip-text ${isDarkMode ? 'bg-gradient-to-r from-sky-300 to-sky-200' : 'bg-gradient-to-r from-sky-700 to-sky-600'}`}>MMIR Playground</span>
  </div>
);

// Enlarge Image Modal Component
const EnlargeImageModal = ({ show, onClose, imageUrl }) => {
  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-graphite-gray bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="relative max-w-3xl w-full max-h-[90vh] overflow-auto bg-bone-white rounded-lg">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-1.5 bg-sky-600 text-white rounded-lg hover:bg-sky-700 transition-all duration-200 shadow-md active:scale-[0.99]"
        >
          <X size={20} />
        </button>
        <img
          src={imageUrl}
          alt="Enlarged Preview"
          className="w-full h-auto rounded-lg"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = IMAGE_FALLBACK;
          }}
        />
      </div>
    </div>
  );
};

// Upload Modal Component
const UploadModal = ({ show, onClose, imageUrlInput, setImageUrlInput, displayedImageUrl, setDisplayedImageUrl, openEnlargeModal, imageUploader, setUploadedImageUrl, isDarkMode }) => {
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  const handleImageUrlChange = (e) => {
    const url = e.target.value;
    setImageUrlInput(url);
    if (url.trim() !== '') {
      setDisplayedImageUrl(url);
    } else {
      setDisplayedImageUrl('');
    }
  };
  
  const handleFileSelect = async (file) => {
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setDisplayedImageUrl(e.target.result);
      };
      reader.readAsDataURL(file);
      
      // Upload to S3
      setIsUploading(true);
      try {
        const s3Url = await imageUploader.uploadImageToS3(file);
        setImageUrlInput(s3Url);
      } catch (error) {
        console.error('Upload failed:', error);
        // Clear the displayed image and show error
        setDisplayedImageUrl('');
        setImageUrlInput('');
        alert('Upload failed, try again with another image or use an image from the web');
      } finally {
        setIsUploading(false);
      }
    }
  };
  
  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    handleFileSelect(file);
  };
  
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };
  
  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    handleFileSelect(file);
  };
  
  const handlePaste = (e) => {
    const items = e.clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith('image/')) {
        const file = items[i].getAsFile();
        handleFileSelect(file);
        break;
      }
    }
  };
  
  useEffect(() => {
    if (show) {
      document.addEventListener('paste', handlePaste);
      return () => document.removeEventListener('paste', handlePaste);
    }
  }, [show]);
  
  const handleDoneClick = () => {
    setUploadedImageUrl(imageUrlInput || displayedImageUrl);
    onClose();
  };
  
  if (!show) return null;
  
  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4 bg-black/50">
      <div className={`rounded-lg p-8 w-full max-w-md relative ${isDarkMode ? 'bg-[#333333] border border-[#555555]' : 'bg-[#fefefb] border border-[#eeeeea]'}`}>
        <button
          onClick={onClose}
          className="absolute top-4 right-4 bg-sky-600 text-white p-1 rounded-lg hover:bg-sky-700 transition-all duration-200 shadow-md active:scale-[0.99]"
        >
          <X size={16} />
        </button>
        <h2 className={`text-2xl font-medium mb-6 ${isDarkMode ? 'text-[#fafafa]' : 'text-graphite-gray'}`}>Upload Image</h2>

        {/* File Upload Area */}
        <div 
          className={`mb-6 border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
            isDragOver ? 'border-sky-500 bg-sky-50' : 'border-gray-300 hover:border-gray-400'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <Upload className="mx-auto mb-3 text-gray-400" size={32} />
          <p className={`text-sm mb-2 ${isDarkMode ? 'text-[#cccccc]' : 'text-gray-600'}`}>Drag and drop an image, paste from clipboard, or</p>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="px-4 py-2 bg-sky-600 text-white text-sm rounded-lg hover:bg-sky-700 transition-colors"
          >
            Choose File
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileInputChange}
            className="hidden"
          />
        </div>

        {/* URL Input */}
        <div className="mb-6">
          <p className={`text-sm mb-2 text-center ${isDarkMode ? 'text-[#cccccc]' : 'text-gray-600'}`}>Or enter image URL:</p>
          <input
            type="text"
            id="imageUrl"
            className={`w-full px-3 py-3 border rounded-lg focus:outline-none transition-all duration-200 ease-in-out ${isDarkMode ? 'bg-[#404040] border-[#555555] text-[#fafafa] focus:border-sky-300' : 'bg-white border-gray-300 text-graphite-gray focus:border-sky-500'}`}
            placeholder={isInputFocused || imageUrlInput ? '' : 'Enter image URL here'}
            value={imageUrlInput}
            onChange={handleImageUrlChange}
            onFocus={() => setIsInputFocused(true)}
            onBlur={() => setIsInputFocused(false)}
          />
        </div>

        {displayedImageUrl && (
          <div className="mb-6 text-center">
            <p className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-[#fafafa]' : 'text-graphite-gray'}`}>
              {isUploading ? 'Uploading to S3...' : 'Preview:'}
            </p>
            <div className="relative">
              <img
                src={displayedImageUrl}
                alt="Preview"
                className={`w-full h-40 object-cover rounded-lg border border-gray-300 cursor-pointer ${isUploading ? 'opacity-50' : ''}`}
                onClick={() => !isUploading && openEnlargeModal(displayedImageUrl)}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = IMAGE_FALLBACK;
                }}
              />
              {isUploading && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-8 h-8 border-4 border-t-4 border-t-sky-600 border-gray-300 rounded-full animate-spin"></div>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="flex justify-between mt-6">
          {displayedImageUrl && (
            <button
              onClick={() => {
                setImageUrlInput('');
                setDisplayedImageUrl('');
                setUploadedImageUrl('');
              }}
              className="px-3 py-1 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-all duration-200 shadow-md active:scale-[0.99]"
            >
              Clear
            </button>
          )}
          <button
            onClick={handleDoneClick}
            className="px-3 py-1 bg-sky-600 text-white font-medium rounded-lg hover:bg-sky-700 transition-all duration-200 shadow-md active:scale-[0.99] ml-auto"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

// LoadingSpinner.jsx
const LoadingSpinner = ({ loadingText = "Loading...", isDarkMode }) => (
  <div className="flex justify-center text-center py-12">
    <div className={`${isDarkMode ? 'text-white' : 'text-gray-900'} inline-flex items-center`}>
      <svg aria-hidden="true" role="status" className={`inline w-4 h-4 me-3 ${isDarkMode ? 'text-white' : 'text-gray-900'} animate-spin`} viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="#E5E7EB" />
        <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentColor" />
      </svg>
      {loadingText}
    </div>
  </div>
);

const SearchBar = ({ searchTerm, setSearchTerm, openUploadModal, uploadedImageUrl, handleSearchSubmit, imageSelected, setImageSelected, textSelected, setTextSelected, queryIntelligenceSelected, setQueryIntelligenceSelected, isDarkMode }) => {
  return (
      <div className={`flex-grow rounded-xl border flex items-center p-0 mr-4 ${isDarkMode ? 'bg-[#404040] border-sky-300' : 'bg-[#fefefb] border-sky-600'}`}>
        <input
          type="text"
          placeholder="Ask anything..."
          className={`flex-grow placeholder-gray-500 focus:outline-none px-4 py-3 text-md bg-transparent ${isDarkMode ? 'text-[#fafafa]' : 'text-graphite-gray'}`}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {uploadedImageUrl && (
          <img
            src={uploadedImageUrl}
            alt="Preview"
            className="w-8 h-8 object-cover rounded-full ml-2 mr-2"
            onError={(e) => { e.target.onerror = null; e.target.src = IMAGE_FALLBACK; }}
          />
        )}
        <div className="flex items-center space-x-2 pr-2">
          <button className={`p-2 cursor-pointer rounded-full transition-all duration-200 ease-in-out active:scale-[0.99] ${isDarkMode ? 'text-[#fafafa] hover:text-sky-300' : 'text-gray-800 hover:text-sky-800'}`} onClick={openUploadModal}>
            <Upload size={20} />
          </button>
          
          <div className={`flex rounded-xl p-1 ${isDarkMode ? 'bg-[#242424]' : 'bg-[#f3f3ed]'}`}>
            <button 
              className={`p-1 mr-1 rounded-md transition-all duration-200 ease-in-out relative group ${
                !uploadedImageUrl ? (isDarkMode ? 'text-gray-500 cursor-not-allowed' : 'text-gray-400 cursor-not-allowed') : 
                imageSelected ? (isDarkMode ? 'border-1 border-sky-300 bg-sky-900 text-sky-300 shadow-md' : 'border-1 border-sky-800 bg-sky-100 text-sky-800 shadow-md') : (isDarkMode ? 'text-gray-300' : 'text-gray-600')
              }`}
              onClick={() => uploadedImageUrl && setImageSelected(!imageSelected)}
              disabled={!uploadedImageUrl}
            >
              <ImageDown size={18} />
              <div className={`absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 text-xs rounded shadow-lg whitespace-nowrap z-20 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-200 ${isDarkMode ? 'bg-gray-200 text-black' : 'bg-gray-800 text-white'}`}>
                {!uploadedImageUrl ? 'upload image first' : 'ground with images'}
              </div>
            </button>
            <button 
              className={`p-1 mr-1 rounded-md transition-all duration-200 ease-in-out relative group ${
                uploadedImageUrl ? (isDarkMode ? 'text-gray-500 cursor-not-allowed' : 'text-gray-400 cursor-not-allowed') :
                textSelected ? (isDarkMode ? 'border-1 border-sky-300 bg-sky-900 text-sky-300 shadow-md' : 'border-1 border-sky-800 bg-sky-100 text-sky-800 shadow-md') : (isDarkMode ? 'text-gray-300' : 'text-gray-600')
              }`}
              onClick={() => !uploadedImageUrl && setTextSelected(!textSelected)}
              disabled={!!uploadedImageUrl}
            >
              <FileText size={18} />
              <div className={`absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 text-xs rounded shadow-lg whitespace-nowrap z-20 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-200 ${isDarkMode ? 'bg-gray-200 text-black' : 'bg-gray-800 text-white'}`}>
                {uploadedImageUrl ? 'disabled in VQA mode' : 'ground with text'}
              </div>
            </button>
            <button 
              className={`p-1 mr-1 rounded-md transition-all duration-200 ease-in-out relative group ${
                uploadedImageUrl ? (isDarkMode ? 'text-gray-500 cursor-not-allowed' : 'text-gray-400 cursor-not-allowed') :
                queryIntelligenceSelected ? (isDarkMode ? 'border-1 border-sky-300 bg-sky-900 text-sky-300 shadow-md' : 'border-1 border-sky-800 bg-sky-100 text-sky-800 shadow-md') : (isDarkMode ? 'text-gray-300' : 'text-gray-600')
              }`}
              onClick={() => !uploadedImageUrl && setQueryIntelligenceSelected(!queryIntelligenceSelected)}
              disabled={!!uploadedImageUrl}
            >
              <BrainCircuit size={18} />
              <div className={`absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 text-xs rounded shadow-lg whitespace-nowrap z-20 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-200 ${isDarkMode ? 'bg-gray-200 text-black' : 'bg-gray-800 text-white'}`}>
                {uploadedImageUrl ? 'disabled in VQA mode' : 'query intelligence'}
              </div>
            </button>
          </div>
          
          <button onClick={() => handleSearchSubmit(true)} className={`p-2 cursor-pointer rounded-full transition-all duration-200 ease-in-out active:scale-[0.99] ${isDarkMode ? 'text-[#fafafa] hover:text-sky-300' : 'text-gray-800 hover:text-sky-800'}`}>
            <ArrowRight size={20} />
          </button>
        </div>
      </div>
  );
};

// Initial Search Screen (adapted to match header search bar)
const InitialSearchScreen = ({ searchTerm, setSearchTerm, errorMessage, handleSearchSubmit, openUploadModal, uploadedImageUrl, imageSelected, setImageSelected, textSelected, setTextSelected, queryIntelligenceSelected, setQueryIntelligenceSelected, handleSuggestionClick, isDarkMode, setIsDarkMode }) => (
  <div className={`flex flex-col items-center justify-center h-screen overflow-hidden ${isDarkMode ? 'bg-[#242424] text-[#fcfcf9]' : 'bg-[#f3f3ed] text-graphite-gray'}`}>
    <div className={`rounded-lg p-8 ml-[70px] mr-[10px] mt-[10px] mb-[10px] w-[calc(100%-90px)] flex flex-col items-center justify-center h-[calc(100%-30px)] relative ${isDarkMode ? 'bg-[#333333]' : 'bg-[#fcfcf9]'}`}>
      <div className="absolute top-4 right-4">
        <button 
          onClick={() => setIsDarkMode(!isDarkMode)}
          className={`px-3 py-1 text-xs rounded-md transition-colors duration-200 ${isDarkMode ? 'bg-[#242424] text-[#fcfcf9] hover:[#000000]' : 'bg-[#f3f3ed] text-graphite-gray hover:bg-[#e1e4dc]'}`}
        >
          {isDarkMode ? 'Light Mode' : 'Dark Mode'}
        </button>
      </div>
    <div className="mb-2">
      <VisualQALogo isDarkMode={isDarkMode} />
    </div>
    <div className="w-full max-w-2xl flex items-center p-2 mb-2 mt-4">
      <SearchBar 
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        handleSearchSubmit={handleSearchSubmit}
        openUploadModal={openUploadModal}
        uploadedImageUrl={uploadedImageUrl}
        imageSelected={imageSelected}
        setImageSelected={setImageSelected}
        textSelected={textSelected}
        setTextSelected={setTextSelected}
        queryIntelligenceSelected={queryIntelligenceSelected}
        setQueryIntelligenceSelected={setQueryIntelligenceSelected}
        isDarkMode={isDarkMode}
      />
    </div>
    {errorMessage && (
      <p className="text-red-500 text-sm mt-2 leading-relaxed">
        {errorMessage}
      </p>
    )}
    <div className="w-full max-w-2xl mt-4 mb-8">
      <div className="flex flex-wrap gap-2 justify-center">
        {suggestedQueries().map((suggestion, index) => (
          <button
            key={index}
            onClick={() => handleSuggestionClick(suggestion.query)}
            className={`px-3 py-1 text-xs rounded-full transition-all duration-200 ${isDarkMode ? 'bg-gradient-to-r from-sky-300 to-sky-200 hover:from-sky-400 hover:to-sky-300 text-black' : 'bg-gradient-to-r from-sky-700 to-sky-600 hover:from-sky-800 hover:to-sky-700 text-white'}`}
          >
            {suggestion.query}
          </button>
        ))}
      </div>
    </div>
      <div className={`absolute bottom-12 left-0 right-0 flex justify-center text-xs text-center ${isDarkMode ? 'text-[#fafafa]' : 'text-graphite-gray'}`}>
        <p>Built by Multimodal Information Retrieval (MMIR) team at Amazon<br />across Arlington, Berlin, Boston, London, Los Angeles, San Francisco Bay Area, and Seattle</p>
      </div>
    </div>
  </div>
);

// Main Interaction Screen (combining header and dynamic content)
const MainInteractionScreen = ({ searchTerm, setSearchTerm, errorMessage, handleSearchSubmit, openUploadModal, uploadedImageUrl, setShowResults, qaResponses, setQaResponses, imageSelected, setImageSelected, textSelected, setTextSelected, queryIntelligenceSelected, setQueryIntelligenceSelected, isDarkMode, setIsDarkMode, openAnnotateModal, hasSearched, setHasSearched, hasSearchedWithoutImage, currentTabQueryParam, updateURLParams, videoFilters, setVideoFilters }) => {
  const [activeTab, setActiveTab] = useState(() => {
    if (uploadedImageUrl) return 'vqa';
    if (currentTabQueryParam === 'vqa' && !uploadedImageUrl) return 'images';
    return currentTabQueryParam || 'images';
  });
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [showSidebar, setShowSidebar] = useState(false);
  const [currentItemIndex, setCurrentItemIndex] = useState(null);
  const [galleryView, setGalleryView] = useState(false);
  // Store the displayed answer at this level to persist across tab changes
  const [persistedAnswer, setPersistedAnswer] = useState('');
  const [hasTyped, setHasTyped] = useState(false);
  const [typingProgress, setTypingProgress] = useState(0);
  const [wordBoundaries, setWordBoundaries] = useState([]);
  const [expandedEntities, setExpandedEntities] = useState(new Set());
  const [showFilters, setShowFilters] = useState(false);
  const [filteredImages, setFilteredImages] = useState([]);
  const [filteredVideos, setFilteredVideos] = useState([]);
  const [filters, setFilters] = useState({
    size: 'Size',
    sourceType: 'Source type',
    imageType: 'Image type',
    time: 'Any time'
  });
  const [activeDropdown, setActiveDropdown] = useState(null);

  const filterOptions = {
    size: ['All', 'Large', 'Medium', 'Small'],
    sourceType: ['All', 'Wikipedia', 'Getty', 'IMDB', 'Web'],
    imageType: ['All', 'JPEG', 'PNG'],
    time: ['Any time', 'Past 24 hours', 'Past week', 'Past month', 'Past year']
  };

  const videoFilterOptions = {
    profile: VIDEO_PROFILES
  };

  const toggleDropdown = (filterName) => {
    setActiveDropdown(activeDropdown === filterName ? null : filterName);
  };

  const selectOption = (filterName, option) => {
    if (activeTab === 'images') {
      setFilters(prev => ({
        ...prev,
        [filterName]: option === 'All' || option === 'Any time' 
          ? filterName.charAt(0).toUpperCase() + filterName.slice(1).replace(/([A-Z])/g, ' $1').toLowerCase()
          : option
      }));
    } else {
      setVideoFilters(prev => ({ ...prev, [filterName]: option }));
    }
    setActiveDropdown(null);
  };

  const FilterDropdown = ({ name, label, options }) => (
    <div className="relative">
      <button
        onClick={() => toggleDropdown(name)}
        className={`flex items-center space-x-1 px-4 py-2 ${isDarkMode ? 'text-white hover:text-gray-300' : 'text-gray-800 hover:text-gray-600'} transition-colors duration-200 text-sm`}
      >
        <span>{activeTab === 'images' ? filters[name] : videoFilters[name]}</span>
        <ChevronDown className="w-4 h-4" />
      </button>
      
      {activeDropdown === name && (
        <div className={`absolute top-full left-0 mt-1 ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'} border rounded-md shadow-lg z-50 min-w-48`}>
          {options.map((option, index) => (
            <button
              key={index}
              onClick={() => selectOption(name, option)}
              className={`block w-full text-left px-4 py-2 text-sm ${isDarkMode ? 'text-gray-200 hover:bg-gray-600' : 'text-gray-700 hover:bg-gray-100'} first:rounded-t-md last:rounded-b-md`}
            >
              {option}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  const getEntityInfoForCaptionText = useCallback((caption) => {
    // Mock function - replace with actual implementation
    return Promise.resolve([]);
  }, []);

  const scrollToEntity = (entityIndex) => {
    const entityElement = document.getElementById(`entity-${entityIndex}`);
    if (entityElement) {
      entityElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (!expandedEntities.has(entityIndex)) {
        toggleEntity(entityIndex);
      }
    }
  };

  const toggleEntity = (entityIndex) => {
    const newExpanded = new Set(expandedEntities);
    if (newExpanded.has(entityIndex)) {
      newExpanded.delete(entityIndex);
    } else {
      newExpanded.add(entityIndex);
    }
    setExpandedEntities(newExpanded);
  };

  useEffect(() => {
    setWordBoundaries([]);
  }, [selectedMedia]);

  useEffect(() => {
    if (!qaResponses?.mmir_images_aug?.length) return;

    let filtered = [...qaResponses.mmir_images_aug];
    
    if (filters.imageType !== 'All' && filters.imageType !== 'Image type') {
      filtered = filtered.filter((image) => image.image_format?.toLowerCase() === filters.imageType.toLowerCase());
    }
    if (filters.size !== 'All' && filters.size !== 'Size') {
      if (filters.size === 'Small') {
        filtered = filtered.filter((image) => (image.width < 300 || image.height < 300));
      } else if (filters.size === 'Medium') {
        filtered = filtered.filter((image) => ((image.width >= 300 && image.width < 1000) || (image.height >= 300 && image.height < 1000)));
      } else if (filters.size === 'Large') {
        filtered = filtered.filter((image) => (image.width >= 1000 || image.height >= 1000));
      }
    }
    if (filters.sourceType !== 'All' && filters.sourceType !== 'Source type') {
      let selectedSourceType = filters.sourceType === 'Web' ? 'Opengraph' : filters.sourceType;
      if (selectedSourceType === 'Getty') {
        filtered = filtered.filter((image) => (image.source_text?.toLowerCase() === selectedSourceType.toLowerCase() || image.source_text?.toLowerCase() === 'getty images'));
      } else {
        filtered = filtered.filter((image) => image.source_text?.toLowerCase() === selectedSourceType.toLowerCase());
      }
    }
    
    setFilteredImages(filtered);
  }, [qaResponses?.mmir_images_aug, filters]);

  useEffect(() => {
    setFilteredVideos(qaResponses?.mmir_videos_aug || []);
  }, [qaResponses?.mmir_videos_aug]);

  const handleSeeMoreImages = () => {
    setActiveTab('images');
    setGalleryView(true);
  };

  const handleSeeMoreVideos = () => {
    setActiveTab('videos');
    setGalleryView(true);
  };

  const handleMediaClick = (media, index) => {
    setSelectedMedia(media);
    setCurrentItemIndex(index);
    setShowSidebar(true);
  };

  const closeSidebar = () => {
    setShowSidebar(false);
    setSelectedMedia(null);
    setCurrentItemIndex(null);
  };

  const navigateGallery = (direction) => {
    const items = activeTab === 'images' ? qaResponses?.mmir_images_aug : qaResponses?.mmir_videos_aug;
    if (!items || items.length === 0) return;
    
    const newIndex = (currentItemIndex + direction + items.length) % items.length;
    setCurrentItemIndex(newIndex);
    setSelectedMedia({...items[newIndex], type: activeTab === 'images' ? 'image' : 'video'});
  };

  const GalleryItemComponent = ({ media, index, onClick }) => (
    <div 
      className={`group cursor-pointer w-1/6 p-2 transition-all duration-200 rounded-lg ${(galleryView && currentItemIndex === index) ? (isDarkMode ? 'bg-gray-600 ring-1 ring-sky-400' : 'bg-slate-100 ring-1 ring-sky-600') : ''}`}
      onClick={() => onClick(media, index)}
    >
      <div className="w-full overflow-hidden transition-shadow duration-200 h-full p-2">
        {media.type === 'image' ? (
          <div className="h-48 w-full overflow-hidden rounded mb-2">
            <img 
              src={media.url}
              alt={media.source_metadata?.title || 'Image'}
              className="h-full w-full object-cover transition-transform duration-300"
              loading="lazy"
            />
          </div>
        ) : (
          <div className="relative h-48 w-full overflow-hidden rounded mb-2">
            {media.source_type?.toLowerCase() === 'youtube' ? (
              <iframe
                src={`https://www.youtube.com/embed/${media.source_url?.split('v=')[1]?.split('&')[0] || ''}`}
                title={media.source_metadata?.title || 'YouTube video'}
                className="h-full w-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <>
                <img 
                  src={media.source_metadata?.thumbnail_url}
                  alt={media.source_metadata?.title || 'Video thumbnail'}
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <Play size={40} className="text-white opacity-75" />
                </div>
              </>
            )}
          </div>
        )}
        <div className="px-1 text-left space-y-1">
          <div className="flex items-center space-x-1">
            <Globe size={10} className="text-gray-500 flex-shrink-0" />
            <p className={`truncate text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {media.type === 'image' ? 
                (media.attribution_url ? new URL(media.attribution_url).hostname.replace('www.', '') : 'Unknown domain') :
                (media.source_url ? new URL(media.source_url).hostname.replace('www.', '') : 'Unknown domain')
              }
            </p>
          </div>
          <h3 className={`truncate text-sm font-normal ${isDarkMode ? 'text-white' : 'text-gray-800'} hover:underline`}>
            {media.type === 'image' ? (media.source_metadata?.title || media.source_metadata?.caption) : media.source_metadata?.title}
          </h3>
          {media.type === 'video' && (
            <>
              <div className={`mb-2 text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-600'}`}>{media.source_type} • {formatDate(media.published_date)}</div>
              <div className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-700'} line-clamp-4`}>{media.description}</div>
            </>
          )}
          {media.score && (
            <p className="text-xs text-sky-400">Score: {(media.score * 100).toFixed(1)}%</p>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className={`flex flex-col items-center justify-center h-screen overflow-hidden ${isDarkMode ? 'bg-[#242424] text-[#fcfcf9]' : 'bg-[#f3f3ed] text-graphite-gray'}`}>
      <div className={`rounded-lg p-8 ml-[70px] mr-[10px] mt-[10px] mb-[10px] w-[calc(100%-90px)] flex flex-col items-center justify-center h-[calc(100%-30px)] relative ${isDarkMode ? 'bg-[#333333]' : 'bg-[#fcfcf9]'}`}>
        <Header
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          errorMessage={errorMessage}
          handleSearchSubmit={handleSearchSubmit}
          openUploadModal={openUploadModal}
          uploadedImageUrl={uploadedImageUrl}
          setShowResults={setShowResults}
          imageSelected={imageSelected}
          setImageSelected={setImageSelected}
          textSelected={textSelected}
          setTextSelected={setTextSelected}
          queryIntelligenceSelected={queryIntelligenceSelected}
          setQueryIntelligenceSelected={setQueryIntelligenceSelected}
          isDarkMode={isDarkMode}
          setIsDarkMode={setIsDarkMode}
          openAnnotateModal={openAnnotateModal}
          qaResponses={qaResponses}
        />

        <div className="w-full flex justify-center mb-4 relative">
          <div className="flex">
            {hasSearched && uploadedImageUrl && (
              <button
                className={`px-4 py-2 ${activeTab === 'vqa' ? `${isDarkMode ? 'text-sky-300 border-sky-300' : 'text-sky-600 border-sky-600'} border-b-2` : 'text-gray-500'}`}
                onClick={() => {
                  setActiveTab('vqa');
                  updateURLParams(searchTerm, textSelected, 'vqa', queryIntelligenceSelected, uploadedImageUrl);
                }}
              >
                VQA Mode
              </button>
            )}
            <button
              className={`px-4 py-2 ${activeTab === 'images' ? `${isDarkMode ? 'text-sky-300 border-sky-300' : 'text-sky-600 border-sky-600'} border-b-2` : 'text-gray-500'}`}
              onClick={() => {
                setActiveTab('images');
                updateURLParams(searchTerm, textSelected, 'images', queryIntelligenceSelected, uploadedImageUrl);
                // Close grounding console and sidebar when switching from VQA
                if (activeTab === 'vqa') {
                  const event = new CustomEvent('closeGroundingConsole');
                  window.dispatchEvent(event);
                }
              }}
            >
              Images {qaResponses?.mmir_images_aug?.length > 0 && `(${qaResponses.mmir_images_aug.length})`}
            </button>
            <button
              className={`px-4 py-2 ${activeTab === 'videos' ? `${isDarkMode ? 'text-sky-300 border-sky-300' : 'text-sky-600 border-sky-600'} border-b-2` : 'text-gray-500'}`}
              onClick={() => {
                setActiveTab('videos');
                updateURLParams(searchTerm, textSelected, 'videos', queryIntelligenceSelected, uploadedImageUrl);
                // Close grounding console and sidebar when switching from VQA
                if (activeTab === 'vqa') {
                  const event = new CustomEvent('closeGroundingConsole');
                  window.dispatchEvent(event);
                }
              }}
            >
              Videos {qaResponses?.mmir_videos_aug?.length > 0 && `(${qaResponses.mmir_videos_aug.length})`}
            </button>
          </div>
          {(qaResponses?.mmir_images_aug?.length > 0 || qaResponses?.mmir_videos_aug?.length > 0) && (activeTab === 'images' || activeTab === 'videos') && (
            <button
              className={`absolute right-0 px-4 py-2 font-sm font-normal ${isDarkMode ? 'text-gray-500 hover:text-white' : 'text-gray-600 hover:text-gray-800'}`}
              onClick={() => setShowFilters(!showFilters)}
            >
              Tools
            </button>
          )}
        </div>
        
        {showFilters && activeTab === 'images' && (
          <div className="w-full relative z-50 mb-4">
            <div className="flex w-full py-0">
              <div className="flex justify-end w-full">
                <div className="flex">
                  <FilterDropdown name="size" label="Size" options={filterOptions.size} />
                  <FilterDropdown name="sourceType" label="Source type" options={filterOptions.sourceType} />
                  <FilterDropdown name="imageType" label="Image type" options={filterOptions.imageType} />
                  <FilterDropdown name="time" label="Any time" options={filterOptions.time} />
                </div>
              </div>
            </div>
            {activeDropdown && (
              <div 
                className="fixed inset-0 z-40" 
                onClick={() => setActiveDropdown(null)}
              />
            )}
          </div>
        )}
        
        {showFilters && activeTab === 'videos' && (
          <div className="w-full relative z-50 mb-4">
            <div className="flex w-full py-0">
              <div className="flex justify-end w-full">
                <div className="flex">
                  <FilterDropdown name="profile" label="Profile" options={videoFilterOptions.profile} />
                </div>
              </div>
            </div>
            {activeDropdown && (
              <div 
                className="fixed inset-0 z-40" 
                onClick={() => setActiveDropdown(null)}
              />
            )}
          </div>
        )}

        <div className={`overflow-y-auto scrollbar-hide h-screen flex flex-col pb-36 w-full ${showFilters ? 'mt-4' : ''}`}>
          {qaResponses?.mmlm_answer && !uploadedImageUrl && (activeTab === 'images' || activeTab === 'videos') && (
            <div className="mb-6">
              <EditableTextToggleDisplay 
                answer={qaResponses.mmlm_answer} 
                handleLLMAnswerChange={(newAnswer) => {
                  setQaResponses(prev => ({...prev, mmlm_answer: newAnswer}));
                }} 
                subqueries={[]}
                isDarkMode={isDarkMode}
              />
            </div>
          )}
          {activeTab === 'vqa' && (
            <div>
              <VtagComparisonView
                qaResponses={qaResponses}
                isDarkMode={isDarkMode}
              />
            </div>
          )}
          {activeTab === 'images' && (
            <div className="flex flex-row h-screen">
              <div className={`overflow-y-auto scrollbar-hide h-screen flex flex-col pb-36 ${(galleryView && showSidebar && selectedMedia) ? 'w-3/4 pr-4' : 'w-full'}`}>
                <div className='flex flex-wrap p-2 w-full'>
                  {filteredImages?.map((image, index) => (
                    <GalleryItemComponent 
                      key={index}
                      media={{...image, type: 'image'}}
                      index={index}
                      onClick={(media, index) => {
                        setSelectedMedia(media);
                        setCurrentItemIndex(index);
                        setShowSidebar(true);
                        setGalleryView(true);
                      }}
                    />
                  ))}
                  {filteredImages?.length === 0 && (
                    <div className="text-center py-10 w-full">
                      <p className="text-gray-500">No images found</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Rounded Sidebar with Blur Shadow */}
              {galleryView && showSidebar && selectedMedia && (
                <div className={`w-1/4 pl-2 ${isDarkMode ? 'bg-[#333333]' : 'bg-[#fcfcf9]'}`}>
                  <div className={`fixed ${showFilters ? 'top-[220px]' : 'top-[180px]'} bottom-[50px] w-[calc(25%-3rem)] overflow-y-auto ${isDarkMode ? 'bg-[#333333] text-white' : 'bg-[#fcfcf9] text-gray-800'}`}>
                    <div className="p-4">
                    <div className="relative">
                      {/* Header with navigation */}
                      <div className="flex items-center justify-between mb-4 p-2">
                        <div className="flex items-center space-x-2">
                          <Globe className="w-4 h-4 text-gray-500" />
                          <span className="text-sm font-medium">
                            {selectedMedia.attribution_url ? new URL(selectedMedia.attribution_url).host.replace('www.', '') : 'Unknown'}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button className={`p-1 ${isDarkMode ? 'text-sky-300 hover:text-sky-200' : 'text-sky-600 hover:text-sky-700'}`}>
                            <a href={selectedMedia.url} download="image">
                              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" />
                              </svg>
                            </a>
                          </button>
                          <button
                            onClick={() => navigateGallery(-1)}
                            className={`p-1 rounded-full transition-colors ${isDarkMode ? 'hover:bg-gray-700 text-white' : 'hover:bg-gray-200 text-gray-600'}`}
                          >
                            <ChevronLeft className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => navigateGallery(1)}
                            className={`p-1 rounded-full transition-colors ${isDarkMode ? 'hover:bg-gray-700 text-white' : 'hover:bg-gray-200 text-gray-600'}`}
                          >
                            <ChevronRight className="w-5 h-5" />
                          </button>
                          <button
                            onClick={closeSidebar}
                            className={`p-1 rounded-full transition-colors ${isDarkMode ? 'hover:bg-gray-700 text-white' : 'hover:bg-gray-200 text-gray-600'}`}
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                      </div>

                      {/* Image */}
                      <div className="mb-4">
                        <img
                          src={selectedMedia.url}
                          alt={selectedMedia.source_metadata?.title}
                          className="w-full h-64 object-cover"
                        />
                      </div>

                      {/* Caption and Metadata */}
                      <div className={`mt-4 p-3 rounded ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                        <h4 className="font-medium mb-3">
                          <TextWithEntities
                            text={selectedMedia.source_metadata?.title || selectedMedia.source_metadata?.caption}
                            getEntityInfo={getEntityInfoForCaptionText}
                            wordBoundaries={wordBoundaries}
                            setWordBoundaries={setWordBoundaries}
                            scrollToEntity={scrollToEntity}
                          />
                        </h4>
                        {selectedMedia.source_metadata?.title && selectedMedia.source_metadata?.caption && (
                          <div className="text-sm font-light mb-3 text-gray-500">
                            <TextWithEntities
                              text={selectedMedia.source_metadata.caption}
                              getEntityInfo={getEntityInfoForCaptionText}
                              wordBoundaries={wordBoundaries}
                              setWordBoundaries={setWordBoundaries}
                              scrollToEntity={scrollToEntity}
                            />
                          </div>
                        )}
                        <div className="space-y-2">
                          {selectedMedia.height && selectedMedia.width && (
                            <div className="flex items-center space-x-3">
                              <Hash className="w-4 h-4 text-gray-500" />
                              <span className="text-sm text-gray-400">Dimensions:</span>
                              <span className="text-sm font-medium">{selectedMedia.height + "x" + selectedMedia.width}</span>
                            </div>
                          )}

                          <div className="flex items-center space-x-3">
                            <Globe className="w-4 h-4 text-gray-500" />
                            <span className="text-sm text-gray-400">Format:</span>
                            <span className="text-sm font-medium">{selectedMedia.image_format}</span>
                          </div>

                          <div className="flex items-center space-x-3">
                            <Calendar className="w-4 h-4 text-gray-500" />
                            <span className="text-sm text-gray-400">Created:</span>
                            <span className="text-sm font-medium">{formatDate(selectedMedia.source_metadata?.creation_date)}</span>
                          </div>

                          <div className="flex items-center space-x-3">
                            <Calendar className="w-4 h-4 text-gray-500" />
                            <span className="text-sm text-gray-400">Ingested:</span>
                            <span className="text-sm font-medium">{formatDate(selectedMedia.source_metadata?.ingestion_date)}</span>
                          </div>

                          {selectedMedia.attribution_url && (
                            <div className="flex items-center space-x-3">
                              <Globe className="w-4 h-4 text-gray-500" />
                              <span className="text-sm text-gray-400">Source:</span>
                              <span className="text-sm font-medium">{selectedMedia.attribution_url ? (new URL(selectedMedia.attribution_url).host.replace('www.', '')) : selectedMedia.attribution_text}</span>
                            </div>
                          )}

                          {selectedMedia.query_intelligence_subquery && (
                            <div className="flex items-center space-x-3">
                              <Brain className="w-4 h-4 text-gray-500" />
                              <span className="text-sm text-gray-400">Query:</span>
                              <span className="text-sm font-medium">
                                {selectedMedia.query_intelligence_subquery}
                              </span>
                            </div>
                          )}
                        </div>
                        
                        {selectedMedia.entities && selectedMedia.entities.length > 0 && (
                          <div>
                            <h4 className={`font-medium mb-3 ${isDarkMode ? 'text-sky-300' : 'text-sky-600'}`}>Entities ({selectedMedia.entities.length})</h4>
                            <div className="space-y-3">
                              {selectedMedia.entities.map((ent, index) => (
                                <div key={index} className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                                  <h5 className={`font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{ent.title}</h5>
                                  <p className={`text-sm mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{ent.description}</p>
                                  <div className="text-xs text-gray-400">
                                    <span className={`px-2 py-1 rounded ${isDarkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>ID: {ent.id}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {selectedMedia.url && (
                          <div className={`mt-6 pt-4 border-t ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                            <a
                              href={selectedMedia.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center space-x-2 text-sky-600 hover:text-sky-500 transition-colors"
                            >
                              <Globe className="w-4 h-4" />
                              <span className="text-sm font-medium">View Original Source</span>
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          {activeTab === 'videos' && (
            <div className="flex flex-row h-screen">
              <div className="overflow-y-auto scrollbar-hide h-screen flex flex-col pb-36 w-full">
                <div className='flex flex-wrap p-2 w-full'>
                  {filteredVideos?.map((video, index) => (
                    <GalleryItemComponent 
                      key={index}
                      media={{...video, type: 'video'}}
                      index={index}
                      onClick={() => {
                        if (video.source_url) {
                          window.open(video.source_url, '_blank');
                        }
                      }}
                    />
                  ))}
                  {filteredVideos?.length === 0 && (
                    <div className="text-center py-10 w-full">
                      <p className="text-gray-500">No videos found</p>
                    </div>
                  )}
                </div>
              </div>


            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Common Header component for interaction screens
const Header = ({ searchTerm, setSearchTerm, errorMessage, handleSearchSubmit, openUploadModal, uploadedImageUrl, setShowResults, imageSelected, setImageSelected, textSelected, setTextSelected, queryIntelligenceSelected, setQueryIntelligenceSelected, isDarkMode, setIsDarkMode, openAnnotateModal, qaResponses }) => (
  <div className="flex flex-col items-center w-full text-graphite-gray px-4 pt-4 pb-2">
    {/* Logo and search bar in same row */}
    <div className="w-full max-w-[calc(100%-120px)] sm:max-w-[calc(100%-100px)] flex items-center justify-center mb-4 gap-4">
      <VisualQALogo isDarkMode={isDarkMode} />
      <SearchBar 
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        handleSearchSubmit={handleSearchSubmit}
        openUploadModal={openUploadModal}
        uploadedImageUrl={uploadedImageUrl}
        imageSelected={imageSelected}
        setImageSelected={setImageSelected}
        textSelected={textSelected}
        setTextSelected={setTextSelected}
        queryIntelligenceSelected={queryIntelligenceSelected}
        setQueryIntelligenceSelected={setQueryIntelligenceSelected}
        isDarkMode={isDarkMode}
      />
      <div className="flex flex-col gap-2">
        <button 
          onClick={() => setIsDarkMode(!isDarkMode)}
          className={`w-24 px-3 py-1 text-xs rounded-md transition-colors duration-200 ${isDarkMode ? 'bg-[#242424] text-[#fcfcf9] hover:[#000000]' : 'bg-[#f3f3ed] text-graphite-gray hover:bg-[#e1e4dc]'}`}
        >
          {isDarkMode ? 'Light Mode' : 'Dark Mode'}
        </button>
        {(uploadedImageUrl || qaResponses?.mmlm_answer) && (
          <button
            onClick={openAnnotateModal}
            className={`w-24 px-2 py-1 text-xs rounded-md transition-colors duration-200 ${isDarkMode ? 'bg-gradient-to-r from-sky-300 to-sky-200 text-[#242424] hover:from-sky-400 hover:to-sky-300' : 'bg-gradient-to-r from-sky-700 to-sky-600 text-white hover:from-sky-800 hover:to-sky-700'}`}
            title="Annotate" >
              Annotate
          </button>
        )}
      </div>
    </div>
    {errorMessage && (
      <p className="text-red-500 text-sm mt-2 leading-relaxed">
        {errorMessage}
      </p>
    )}
  </div>
);

// MediaViewerModal component for full-screen image/video viewing
const MediaViewerModal = ({ isOpen, media, initialIndex, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  if (!isOpen || !media || media.length === 0) return null;

  const currentMedia = media[currentIndex];

  const goToNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % media.length);
  };

  const goToPrev = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + media.length) % media.length);
  };

  const handleThumbnailClick = (index) => {
    setCurrentIndex(index);
  };

  return (
    <div className="fixed inset-0 flex flex-col items-center justify-center z-[100] p-4 bg-black bg-opacity-70">
      {/* Close Button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 transition-all duration-200 shadow-md"
        aria-label="Close media viewer"
      >
        <X size={20} />
      </button>

      {/* Main Media Content */}
      <div className="flex flex-col items-center justify-center flex-grow w-full max-w-4xl">
        <div className="relative flex flex-col items-center justify-center w-full">
          <div className="flex items-center justify-center w-full h-[calc(100vh-200px)]">
            {currentMedia.type === 'image' ? (
              <img
                src={currentMedia.src}
                alt={currentMedia.caption}
                className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
                onError={(e) => { e.target.onerror = null; e.target.src = IMAGE_FALLBACK; }}
              />
            ) : (
              <div className="relative w-full h-full">
                <iframe 
                  src={currentMedia.source_url} 
                  className="w-full h-full rounded-lg shadow-lg"
                  title={currentMedia.caption || "Video player"}
                  frameBorder="0"
                  referrerPolicy="origin"
                  sandbox="allow-scripts allow-same-origin allow-forms"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  onError={(e) => { 
                    e.target.onerror = null;
                    const fallbackImage = document.createElement('img');
                    fallbackImage.src = IMAGE_FALLBACK;
                    fallbackImage.alt = 'Video unavailable';
                    fallbackImage.className = 'w-full h-full object-contain rounded-lg shadow-lg';
                    e.target.parentNode.replaceChild(fallbackImage, e.target);
                  }}
                  style={{ backgroundColor: '#000' }}
                />
              </div>
            )}
          </div>
          <p className="mt-2 text-white text-base text-center max-w-full overflow-hidden">
            {currentMedia.caption}
          </p>
        </div>
      </div>

      {/* Navigation Arrows for Main Media */}
      {media.length > 1 && (
        <>
          <button
            onClick={goToPrev}
            className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 transition-all duration-200 shadow-md"
            aria-label="Previous media"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={goToNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 transition-all duration-200 shadow-md"
            aria-label="Next media"
          >
            <ChevronRight size={20} />
          </button>
        </>
      )}

      {/* Carousel of all images/videos at the bottom */}
      <div className="w-full max-w-4xl mt-4 overflow-x-auto scrollbar-hide flex justify-center">
        <div className="flex flex-nowrap space-x-2">
          {media.map((item, index) => (
            <div
              key={index}
              className={`flex-none w-32 h-20 relative rounded-md overflow-hidden cursor-pointer
                ${index === currentIndex ? 'border-2 border-sky-500 shadow-lg' : 'border border-gray-300'}
                hover:opacity-80 transition-opacity`}
              onClick={() => handleThumbnailClick(index)}
            >
              <img
                src={item.type === 'image' ? item.src : item.source_metadata?.thumbnail_url}
                alt={`Thumbnail ${index + 1}`}
                className="w-full h-full object-cover"
                onError={(e) => { e.target.onerror = null; e.target.src = IMAGE_FALLBACK; }}
              />
              {item.type === 'video' && (
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
                  <Play size={16} className="text-white opacity-75" />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Expandable text content for grounding hits
const ExpandableContent = ({ value, isDarkMode }) => {
  const [expanded, setExpanded] = useState(false);
  const PREVIEW_LEN = 300;
  const needsTruncation = value.length > PREVIEW_LEN;
  const display = !needsTruncation || expanded ? value : value.slice(0, PREVIEW_LEN) + '...';

  return (
    <div className={`text-xs mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
      {display}
      {needsTruncation && (
        <button
          onClick={() => setExpanded(!expanded)}
          className={`ml-1 text-xs font-medium ${isDarkMode ? 'text-sky-400 hover:text-sky-300' : 'text-sky-600 hover:text-sky-700'}`}
        >
          {expanded ? 'show less' : 'show more'}
        </button>
      )}
    </div>
  );
};

// Single grounding hit card
const GroundingHitCard = ({ hit, index, matchedImage, isDarkMode, onImageClick, serviceColor }) => (
  <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-[#1c1c24]' : 'bg-gray-50'}`} style={{borderLeft: `3px solid ${serviceColor}`}}>
    <div className="flex items-center gap-2 mb-2">
      <span className={`text-xs font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Hit {index + 1}</span>
      <span className="text-xs px-2 py-0.5 rounded" style={{backgroundColor: serviceColor + '22', color: serviceColor}}>{hit.service}</span>
    </div>
    <div className={`flex gap-3 ${matchedImage ? 'flex-row' : 'flex-col'}`}>
      {matchedImage && (
        <div className="flex-shrink-0 cursor-pointer" onClick={() => onImageClick(matchedImage.url)}>
          <img
            src={matchedImage.url}
            alt={matchedImage.id}
            className="w-20 h-20 object-cover rounded-lg border border-gray-600 hover:border-sky-400 transition-colors"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </div>
      )}
      <div className="flex-1">
        {hit.title && <div className="text-xs mb-1"><span className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>Title: </span><span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>{hit.title}</span></div>}
        {hit.text && <ExpandableContent value={hit.text} isDarkMode={isDarkMode} />}
        {hit.domain && <div className="text-xs mb-1"><span className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>Domain: </span><span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>{hit.domain}</span></div>}
        {hit.source_url && hit.source_url !== 'NO_SOURCE' && <div className="text-xs mb-1"><span className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>URL: </span><a href={hit.source_url} target="_blank" rel="noopener noreferrer" className="text-sky-500 hover:underline">{hit.source_url}</a></div>}
      </div>
    </div>
  </div>
);

// VTAG Comparison View — replaces the old MainResultsContent for the demo
const VtagComparisonView = ({ qaResponses, isDarkMode }) => {
  const [groundingExpanded, setGroundingExpanded] = useState(true);
  const [enlargedImageUrl, setEnlargedImageUrl] = useState(null);

  const answerWithout = qaResponses?.answer_without_vtag || '';
  const answerWith = qaResponses?.answer_with_vtag || '';
  const icgImages = qaResponses?.icg_images || [];
  const sections = qaResponses?.vtag_sections || null;

  const serviceColor = (service) => {
    if (service?.includes('ICG')) return 'rgb(34, 197, 94)';
    if (service?.includes('WEB')) return 'rgb(59, 130, 246)';
    if (service?.includes('GRAPH')) return 'rgb(167, 139, 250)';
    return 'rgb(113, 113, 122)';
  };

  const queryTypeLabel = (qt) => {
    if (qt === 'entity_seeking') return 'Entity Seeking';
    if (qt === 'info_seeking') return 'Info Seeking';
    if (qt === 'uncertain') return 'Uncertain';
    return qt || 'unknown';
  };

  const queryTypeColor = (qt) => {
    if (qt === 'entity_seeking') return isDarkMode ? 'text-green-400' : 'text-green-700';
    if (qt === 'info_seeking') return isDarkMode ? 'text-blue-400' : 'text-blue-700';
    return isDarkMode ? 'text-gray-400' : 'text-gray-600';
  };

  const hasContent = sections && (sections.icg_hits?.length > 0 || sections.igs_hits?.length > 0);

  return (
    <div className="w-full space-y-6">
      {/* Side-by-side answer comparison */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className={`rounded-lg p-5 border-t-3 ${isDarkMode ? 'bg-[#2a2a2a] border-red-500' : 'bg-white border-red-500'}`} style={{borderTopWidth: '3px', borderTopColor: '#ef4444'}}>
          <h3 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
            Without VTAG
          </h3>
          <p className={`text-sm leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            {answerWithout || <span className="text-gray-500 italic">Waiting for response...</span>}
          </p>
        </div>
        <div className={`rounded-lg p-5 border-t-3 ${isDarkMode ? 'bg-[#2a2a2a] border-green-500' : 'bg-white border-green-500'}`} style={{borderTopWidth: '3px', borderTopColor: '#22c55e'}}>
          <h3 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
            With VTAG Grounding
          </h3>
          <p className={`text-sm leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            {answerWith || <span className="text-gray-500 italic">Waiting for response...</span>}
          </p>
        </div>
      </div>

      {/* VTAG Grounding Details — structured sections */}
      {(hasContent || sections?.query_type) && (
        <div className={`rounded-lg overflow-hidden ${isDarkMode ? 'bg-[#2a2a2a] border border-[#3f3f46]' : 'bg-white border border-gray-200'}`}>
          <button
            onClick={() => setGroundingExpanded(!groundingExpanded)}
            className={`w-full flex items-center justify-between px-5 py-3 ${isDarkMode ? 'bg-[#333333] hover:bg-[#3a3a3a]' : 'bg-gray-50 hover:bg-gray-100'}`}
          >
            <h3 className={`text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-sky-300' : 'text-sky-700'}`}>
              What VTAG Found
            </h3>
            <ChevronDown size={16} className={`transition-transform ${groundingExpanded ? 'rotate-180' : ''} ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
          </button>
          {groundingExpanded && (
            <div className="p-5 space-y-5">
              {/* 1. Query Classification */}
              {sections?.query_type && (
                <div className={`flex items-center gap-2 px-3 py-2 rounded ${isDarkMode ? 'bg-[#1c1c24]' : 'bg-gray-50'}`}>
                  <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Query Classification:</span>
                  <span className={`text-xs font-semibold ${queryTypeColor(sections.query_type)}`}>{queryTypeLabel(sections.query_type)}</span>
                </div>
              )}

              {/* 2. ICG Results */}
              {sections?.icg_hits?.length > 0 && (
                <div>
                  <h4 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>
                    Visual Grounding (ICG) — {sections.icg_hits.length} result{sections.icg_hits.length !== 1 ? 's' : ''}
                  </h4>
                  <div className="space-y-2">
                    {sections.icg_hits.map((hit, i) => (
                      <GroundingHitCard
                        key={`icg-${i}`}
                        hit={hit}
                        index={i}
                        matchedImage={icgImages[i] || null}
                        isDarkMode={isDarkMode}
                        onImageClick={setEnlargedImageUrl}
                        serviceColor={serviceColor(hit.service)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* 3. Reformulated Query */}
              {sections?.reformulated_query && (
                <div className={`flex items-center gap-2 px-3 py-2 rounded ${isDarkMode ? 'bg-[#1c1c24]' : 'bg-gray-50'}`} style={{borderLeft: '3px solid rgb(167, 139, 250)'}}>
                  <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Reformulated Query:</span>
                  <span className={`text-xs font-semibold ${isDarkMode ? 'text-purple-300' : 'text-purple-700'}`}>"{sections.reformulated_query}"</span>
                </div>
              )}

              {/* 4. IGS Web Results */}
              {sections?.igs_hits?.length > 0 && (
                <div>
                  <h4 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${isDarkMode ? 'text-blue-400' : 'text-blue-700'}`}>
                    Web Search (IGS) — {sections.igs_hits.length} result{sections.igs_hits.length !== 1 ? 's' : ''}
                  </h4>
                  <div className="space-y-2">
                    {sections.igs_hits.map((hit, i) => (
                      <GroundingHitCard
                        key={`igs-${i}`}
                        hit={hit}
                        index={i}
                        matchedImage={null}
                        isDarkMode={isDarkMode}
                        onImageClick={setEnlargedImageUrl}
                        serviceColor={serviceColor(hit.service)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {!hasContent && <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>No grounding results returned.</p>}
            </div>
          )}
        </div>
      )}

      {/* Enlarge image modal */}
      <EnlargeImageModal
        show={!!enlargedImageUrl}
        onClose={() => setEnlargedImageUrl(null)}
        imageUrl={enlargedImageUrl || ''}
      />
    </div>
  );
};

// Main Results Content with Grounding Console
const MainResultsContent = ({ qaResponses, isDarkMode, parentSetActiveTab, persistedAnswer, setPersistedAnswer, hasTyped, setHasTyped, typingProgress, setTypingProgress }) => {
  const [isConsoleOpen, setIsConsoleOpen] = useState(false);
  const [isConsoleCollapsed, setIsConsoleCollapsed] = useState(false);
  
  // Listen for close console events
  useEffect(() => {
    const handleCloseConsole = () => {
      setIsConsoleOpen(false);
      setIsSidebarOpen(false);
      setIsEntitySidebarOpen(false);
    };
    
    window.addEventListener('closeGroundingConsole', handleCloseConsole);
    return () => window.removeEventListener('closeGroundingConsole', handleCloseConsole);
  }, []);
  const [isImageViewerOpen, setIsImageViewerOpen] = useState(false);
  const [currentImageViewerIndex, setCurrentImageViewerIndex] = useState(0);
  const [isVideoViewerOpen, setIsVideoViewerOpen] = useState(false);
  const [currentVideoViewerIndex, setCurrentVideoViewerIndex] = useState(0);
  const [isGroundingImageViewerOpen, setIsGroundingImageViewerOpen] = useState(false);
  const [currentGroundingImageIndex, setCurrentGroundingImageIndex] = useState(0);
  const [showTooltip, setShowTooltip] = useState(false);
  const [showAnimation, setShowAnimation] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [selectedSidebarImage, setSelectedSidebarImage] = useState(null);
  const [isEntitySidebarOpen, setIsEntitySidebarOpen] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [sidebarView, setSidebarView] = useState('image'); // 'image' or 'entity'
  
  // Show tooltip and animation after typing is complete with 1 second delay
  useEffect(() => {
    if (!isTyping) {
      setShowTooltip(true)
      setShowAnimation(true)
      const hideTimer = setTimeout(() => {
          setShowTooltip(false);
          setShowAnimation(false);
        }, 5000);      
      return () => clearTimeout(hideTimer);
    }
  }, [isTyping]);
  
  // Define ref for interval
const typingIntervalRef = useRef(null);

// Set answer text without re-typing when switching tabs
useEffect(() => {
  const answer = qaResponses?.mmlm_answer || 'No answer available';

  if (answer) {
    // If we've already fully typed this answer before, show immediately
    if (hasTyped) {
      setPersistedAnswer(answer);
      setIsTyping(false);
      return;
    }

    // Start or resume typing animation
    setIsTyping(true);
    
    // Start from current progress or from beginning if it's a new answer
    const startPosition = typingProgress > 0 && typingProgress < answer.length ? typingProgress : 0;
    
    typingIntervalRef.current = setInterval(() => {
      const nextPosition = startPosition + 1;
      setTypingProgress(nextPosition);
      setPersistedAnswer(answer.substring(0, nextPosition));
      
      if (nextPosition >= answer.length) {
        clearInterval(typingIntervalRef.current);
        setIsTyping(false);
        setHasTyped(true);
        setIsConsoleOpen(true);
      }
    }, 30);
    
    return () => {
      if (typingIntervalRef.current) {
        clearInterval(typingIntervalRef.current);
      }
    };
  }
}, [qaResponses?.mmlm_answer, hasTyped, setPersistedAnswer, setHasTyped, typingProgress, setTypingProgress]);

// Remove redundant effect since tab visibility is now handled in main effect  
  const handleGroundingConsoleClick = () => {
    setIsConsoleOpen(true);
    setIsConsoleCollapsed(false);
  };

  const handleCloseConsole = () => {
    setIsConsoleOpen(false);
    // Reset main content scale when console closes
    const mainContent = document.querySelector('main');
    if (mainContent) {
      mainContent.style.transform = 'scale(1)';
    }
  };

  const handleToggleCollapseConsole = () => {
    setIsConsoleCollapsed(prev => !prev);
    // Adjust main content scale based on console state
    const mainContent = document.querySelector('main');
    if (mainContent) {
      if (!isConsoleCollapsed) {
        // If collapsing, restore scale
        mainContent.style.transform = 'scale(0.98)';
      } else {
        // If expanding, reduce scale
        mainContent.style.transform = 'scale(0.95)';
      }
    }
  };

  const openImageViewer = (index) => {
    setCurrentImageViewerIndex(index);
    setIsImageViewerOpen(true);
  };

  const closeImageViewer = () => {
    setIsImageViewerOpen(false);
  };
  
  const openVideoViewer = (index) => {
    setCurrentVideoViewerIndex(index);
    setIsVideoViewerOpen(true);
  };

  const closeVideoViewer = () => {
    setIsVideoViewerOpen(false);
  };
  
  const openGroundingImageViewer = (index) => {
    setCurrentGroundingImageIndex(index);
    setIsGroundingImageViewerOpen(true);
  };

  const closeGroundingImageViewer = () => {
    setIsGroundingImageViewerOpen(false);
  };
  
  const openImageSidebar = (image) => {
    setSelectedSidebarImage(image);
    setSidebarView('image');
    setIsSidebarOpen(true);
    setIsEntitySidebarOpen(false);
  };

  const closeImageSidebar = () => {
    setIsSidebarOpen(false);
    setSelectedSidebarImage(null);
  };
  
  const openEntitySidebar = (entity) => {
    setSelectedEntity(entity);
    setSidebarView('entity');
    setIsEntitySidebarOpen(true);
    setIsSidebarOpen(false);
  };

  const closeEntitySidebar = () => {
    setIsEntitySidebarOpen(false);
    setSelectedEntity(null);
  };
  
  // No need for global function anymore as we're passing it directly as a prop

  // Process images from qaResponses for sidebar display
  const images = qaResponses?.mmir_images_aug?.map((item) => ({
    type: 'image',
    src: item.url,
    attribution_url: item.attribution_url,
    caption: item.source_metadata.caption || 'No caption available',
  })) || [];

  // Process videos from qaResponses for sidebar display
  const videos = qaResponses?.mmir_videos_aug?.map((item) => ({
    type: 'video',
    thumbnail_url: item.source_metadata?.thumbnail_url,
    source_url: item.source_url,
    source_type: item.source_type,
    caption: item.source_metadata?.title || 'No caption available',
  })) || [];

  // Process images for grounding console
  const groundingImages = qaResponses?.mmir_images_grounding?.map((item) => ({
    src: item.url,
    caption: item.source_metadata.caption || 'No caption available',
    url: item.url,
    attribution_url: item.attribution_url,
    source_metadata: item.source_metadata,
    entities: item.entities || [],
    ...item
  })) || [];

  return (
    <div className="w-full">
      {/* Main Content Area */}
      <main className="grid grid-cols-1 lg:grid-cols-10 gap-8 overflow-hidden">
        {/* Left Section - Text Content */}
        <section className="lg:col-span-7 space-y-6">
          <div className="sticky top-0 bg-inherit pt-4 pb-2 z-10">
            <h2 className="text-2xl font-bold mb-4 flex items-center">
              <Text className="mr-3" />
              <span className="text-blue font-medium text-xl">Answer</span>
            </h2>
            <div>
              <div className="text-lg leading-relaxed mb-4">
                <UnifiedMarkdownHighlighter 
                  content={persistedAnswer}
                  entityMap={isTyping ? {} : qaResponses?.answer_entity_map || {}}
                  onEntityClick={openEntitySidebar}
                  fullAnswer={qaResponses?.mmlm_answer || ''}
                />
              </div>
            </div>
            <button 
              onClick={handleGroundingConsoleClick}
              className={`px-3 py-1 text-xs rounded-md ${isDarkMode ? 'bg-gradient-to-r from-sky-300 to-sky-200 text-[#242424]' : 'bg-gradient-to-r from-sky-900 to-sky-700 text-[#fafafa]'}`}
              aria-label="Open grounding console"
            >
              <span className={`${showAnimation ? 'animate-pulse' : ''}`}>
                Inspect Grounding
              </span>
              {showTooltip && !isTyping && (
                <div className="absolute left-0 top-full mt-2 px-3 py-1 bg-gray-800 text-white text-xs rounded shadow-lg whitespace-nowrap z-20">
                  click to see image and text grounding
                </div>
              )}
            </button>
          </div>
        </section>
        <section className="lg:col-span-3 space-y-6">
          <h2 className="text-2xl font-bold mb-4 flex items-center">
            <Image className="mr-1"/><span className="text-blue font-medium text-xl">Images (Augmentation)</span>
          </h2>
          <div className="grid grid-cols-2 gap-1 mb-8"> 
            {images.slice(0, 5).map((img, i) => ( 
              <div key={`main-img-${i}`} className="flex flex-col cursor-pointer" onDoubleClick={() => openImageViewer(i)}>
                <div className="rounded overflow-hidden shadow-lg aspect-video">
                  <img
                    src={img.src}
                    alt={img.caption}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.onerror = null; e.target.src = IMAGE_FALLBACK; }}
                  />
                </div>
                <div className="mt-1 mb-2 space-y-1">
                  <div className="flex items-center space-x-1">
                    <Globe size={10} className="text-gray-500 flex-shrink-0" />
                    <p className="text-gray-500 text-xs truncate">{img.attribution_url ? new URL(img.attribution_url).hostname : 'Unknown domain'}</p>
                  </div>
                  <p className="text-gray-800 text-sm truncate">{truncateText(img.caption, 30)}</p>
                </div>
              </div>
            ))}
            {images.length > 5 && (
              <div className="relative rounded overflow-hidden shadow-md aspect-video group cursor-pointer" onClick={() => parentSetActiveTab('images')}> {/* Opens first image in viewer */}
                <img
                  src={images[5]?.src || images[0]?.src || IMAGE_FALLBACK}
                  alt="View More Images"
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.onerror = null; e.target.src = IMAGE_FALLBACK; }}
                />
                <div className="absolute inset-0 bg-black/75 flex items-center justify-center">
                  <button className="flex items-center space-x-1 px-3 py-1 bg-white text-gray-800 text-sm rounded-full shadow-lg hover:bg-gray-300 hover:text-black transition-colors duration-300">
                    <span>View more</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          <h2 className="text-2xl font-bold mb-4 flex items-center">
            <ImagePlay className="mr-1"/><span className="text-blue font-medium text-xl">Videos (Augmentation)</span>
          </h2>
          <div className="grid grid-cols-2 gap-1 mb-8">
            {videos.slice(0, 5).map((vid, i) => (
              <div key={`main-video-${i}`} className="flex flex-col cursor-pointer" onDoubleClick={() => openVideoViewer(i)}>
                <div className="relative rounded overflow-hidden shadow-lg aspect-video group">
                  {vid.source_type?.toLowerCase() === 'youtube' ? (
                    <iframe
                      src={`https://www.youtube.com/embed/${vid.source_url?.split('v=')[1]?.split('&')[0] || ''}`}
                      title={vid.caption || 'YouTube video'}
                      className="w-full h-full"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  ) : (
                    <>
                      <img
                        src={vid.thumbnail_url}
                        alt={vid.caption}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.onerror = null; e.target.src = IMAGE_FALLBACK; }}
                      />
                      <div className="absolute inset-0 flex items-center justify-center transition-all duration-300">
                        <button className="flex items-center space-x-1 px-3 py-1 bg-white text-gray-800 text-sm rounded-full shadow-lg hover:bg-gray-300 hover:text-black transition-colors duration-300">
                          <Play className="mr-1" size={15}/> <span>Watch</span>
                        </button>
                      </div>
                    </>
                  )}
                </div>
                <div className="mt-1 space-y-1">
                  <div className="flex items-center space-x-1">
                    <Globe size={10} className="text-gray-500 flex-shrink-0" />
                    <p className="text-gray-500 text-xs truncate">{vid.source_url ? new URL(vid.source_url).hostname : 'Unknown domain'}</p>
                  </div>
                  <p className="text-gray-800 text-sm truncate">{truncateText(vid.caption, 30)}</p>
                </div>
              </div>
            ))}
            {videos.length > 5 && (
              <div className="relative rounded overflow-hidden shadow-lg aspect-video group cursor-pointer" onClick={() => parentSetActiveTab('videos')}>
                <img
                  src={videos[5]?.thumbnail_url || videos[0]?.thumbnail_url || IMAGE_FALLBACK}
                  alt="View More Videos"
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.onerror = null; e.target.src = IMAGE_FALLBACK; }}
                />
                <div className="absolute inset-0 bg-black/75 flex items-center justify-center">
                  <button className="flex items-center space-x-1 px-3 py-1 bg-white text-gray-800 text-sm rounded-full shadow-lg hover:bg-gray-300 hover:text-black transition-colors duration-300">
                    <span>Watch more</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          <h2 className="text-2xl font-bold mb-4 flex items-center">
            <ChartSpline className="mr-3"/><span className="text-blue font-medium text-xl">Glyph</span>
          </h2>
          <img
            src={IMAGE_FALLBACK}
            alt="Glyph representation"
            className="w-full h-auto rounded-xl"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = IMAGE_FALLBACK;
            }}
          />
        </section>
      </main>

      {/* Grounding Console */}
      <GroundingConsole
        isOpen={isConsoleOpen}
        onClose={handleCloseConsole}
        isCollapsed={isConsoleCollapsed}
        onToggleCollapse={handleToggleCollapseConsole}
        groundingImages={groundingImages}
        groundingText={qaResponses?.ngs_grounding || []}
        openGroundingImageViewer={openGroundingImageViewer}
        openImageSidebar={openImageSidebar}
        qaResponses={qaResponses}
      />
      
      {/* Unified Sidebar */}
      <UnifiedSidebar
        isOpen={isSidebarOpen || isEntitySidebarOpen}
        onClose={() => {
          closeImageSidebar();
          closeEntitySidebar();
        }}
        view={sidebarView}
        setView={setSidebarView}
        image={selectedSidebarImage}
        entity={selectedEntity}
        groundingImages={groundingImages}
        openImageSidebar={openImageSidebar}
      />
      
      {/* Image Viewer Modal */}
      <MediaViewerModal
        isOpen={isImageViewerOpen}
        media={images}
        initialIndex={currentImageViewerIndex}
        onClose={closeImageViewer}
      />

      {/* Video Viewer Modal */}
      <MediaViewerModal
        isOpen={isVideoViewerOpen}
        media={videos}
        initialIndex={currentVideoViewerIndex}
        onClose={closeVideoViewer}
      />
      
      {/* Grounding Image Viewer Modal */}
      <MediaViewerModal
        isOpen={isGroundingImageViewerOpen}
        media={groundingImages.map(img => ({ type: 'image', ...img }))}
        initialIndex={currentGroundingImageIndex}
        onClose={closeGroundingImageViewer}
      />
    </div>
  );
};

// Unified component that handles both markdown rendering and entity highlighting
const UnifiedMarkdownHighlighter = ({ content, entityMap, onEntityClick, fullAnswer }) => {


  const renderHighlightedContent = (text, entityMap, onEntityClick) => {
    if (!text) return null;
    
    // If no entity highlighting needed, just render markdown
    if (!entityMap || Object.keys(entityMap).length === 0) {
      return <div dangerouslySetInnerHTML={{ __html: parseMarkdown(text) }} />;
    }

    // Apply entity highlighting to raw text first
    let highlightedText = text;
    Object.entries(entityMap).forEach(([keyword, data], index) => {
      if (data.entities && keyword && keyword.trim() && text.toLowerCase().includes(keyword.toLowerCase()) && 
          data.entities.some(ent => ent.title || ent.description || ent.id)) {
        const escapedKeyword = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(`\\b${escapedKeyword}\\b`, 'gi');
        highlightedText = highlightedText.replace(regex, (match) => {
          return `__ENTITY_${index}_START__${match}__ENTITY_${index}_END__`;
        });
      }
    });

    // Parse markdown with entity placeholders
    let processedHtml = parseMarkdown(highlightedText);
    
    // Replace placeholders with clickable spans
    Object.entries(entityMap).forEach(([keyword, data], index) => {
      if (data.entities && keyword && keyword.trim() && text.toLowerCase().includes(keyword.toLowerCase()) && 
          data.entities.some(ent => ent.title || ent.description || ent.id)) {
        const startPlaceholder = `__ENTITY_${index}_START__`;
        const endPlaceholder = `__ENTITY_${index}_END__`;
        const regex = new RegExp(`${startPlaceholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(.*?)${endPlaceholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'g');
        processedHtml = processedHtml.replace(regex, (match, entityText) => {
          return `<span class="bg-sky-100 hover:bg-sky-200 text-black cursor-pointer px-1 rounded transition-colors duration-200" data-entity-keyword="${keyword.replace(/"/g, '&quot;')}">${entityText}</span>`;
        });
      }
    });

    return (
      <div 
        dangerouslySetInnerHTML={{ __html: processedHtml }}
        onClick={(e) => {
          const entityKeyword = e.target.getAttribute('data-entity-keyword');
          if (entityKeyword && entityMap[entityKeyword]) {
            onEntityClick({ keyword: entityKeyword, entities: entityMap[entityKeyword].entities });
          }
        }}
      />
    );
  };

  const parseMarkdown = (text) => {
    if (!text) return '';
    
    // First, split numbered lists that are inline
    text = text.replace(/(\d+\.)\s+/g, '\n$1 ');
    // Split dash bullet points that are inline
    text = text.replace(/([^\n])\s+-\s+/g, '$1\n- ');
    // Split star bullet points that are inline
    text = text.replace(/([^\n])\s+\*\s+/g, '$1\n* ');
    // Split headers that are inline
    text = text.replace(/([^\n])\s+(###\s+)/g, '$1\n$2');
    
    const lines = text.split('\n');
    const result = [];
    
    for (let i = 0; i < lines.length; i++) {
      let line = lines[i].trim();
      if (!line) continue;
      
      // Headers (including ### format)
      if (line.startsWith('### ')) {
        result.push(`<h3 class="text-lg font-semibold mt-4 mb-2">${line.slice(4)}</h3>`);
      } else if (line.startsWith('## ')) {
        result.push(`<h2 class="text-xl font-semibold mt-4 mb-2">${line.slice(3)}</h2>`);
      } else if (line.startsWith('# ')) {
        result.push(`<h1 class="text-2xl font-bold mt-4 mb-2">${line.slice(2)}</h1>`);
      }
      // Bullet points (both * and -)
      else if (line.match(/^[-*] /)) {
        const content = line.replace(/^[-*] /, '').replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        result.push(`<div class="flex items-start mb-1"><span class="mr-2">•</span><span>${content}</span></div>`);
      }
      // Numbered lists
      else if (line.match(/^\d+\. /)) {
        line = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        result.push(`<div class="mb-2">${line}</div>`);
      }
      // Regular text
      else {
        // Apply inline formatting
        line = line
          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
          .replace(/\*([^*]+)\*/g, '<em>$1</em>')
          .replace(/\[([^\]]+)\]\(([^\)]+)\)/g, '<a href="$2" class="text-blue-600 hover:text-blue-800 underline" target="_blank">$1</a>');
        
        result.push(`<div class="mb-1">${line}</div>`);
      }
    }
    
    return result.join('');
  };

  return (
    <div className="markdown-content">
      {renderHighlightedContent(content, entityMap, onEntityClick)}
    </div>
  );
};

// UnifiedSidebar component for displaying both image and entity details
const UnifiedSidebar = ({ isOpen, onClose, view, setView, image, entity, groundingImages, openImageSidebar }) => {
  if (!isOpen) return null;

  const uniqueEntities = entity ? entity.entities.filter((ent, index, arr) => 
    arr.findIndex(e => e.id === ent.id) === index && 
    (ent.title || ent.description || ent.id)
  ) : [];
  const relatedImages = entity ? groundingImages.filter(img => {
    return img.entities && img.entities.some(imgEntity => 
      uniqueEntities.some(selectedEntity => 
        imgEntity.id === selectedEntity.id || 
        imgEntity.title === selectedEntity.title
      )
    );
  }) : [];

  return (
    <div className="fixed inset-y-0 right-0 w-96 bg-slate-900 border-l border-slate-700 shadow-xl z-50 transform transition-transform duration-300 ease-in-out flex flex-col">
      <div className="flex items-center justify-between p-4 border-b border-slate-700 bg-slate-800 flex-shrink-0">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setView('image')}
            className={`px-3 py-1 text-sm rounded ${view === 'image' ? 'bg-sky-600 text-white' : 'text-gray-400 hover:text-white'}`}
            disabled={!image}
          >
            Image
          </button>
          <button
            onClick={() => setView('entity')}
            className={`px-3 py-1 text-sm rounded ${view === 'entity' ? 'bg-sky-600 text-white' : 'text-gray-400 hover:text-white'}`}
            disabled={!entity}
          >
            Entity
          </button>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 rounded-full hover:bg-slate-700 text-white transition-colors duration-200"
          aria-label="Close sidebar"
        >
          <X size={16} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-hide">
        {view === 'image' && image && (
          <div className="p-4">
            <div className="mb-4">
              <img
                src={image.url}
                alt={image.source_metadata?.caption || 'Grounding image'}
                className="w-full h-48 object-cover rounded-lg"
                onError={(e) => { e.target.onerror = null; e.target.src = IMAGE_FALLBACK; }}
              />
            </div>
            
            <div className="space-y-4 text-sm mb-6">
              {image.source_url && (
                <div>
                  <div className="text-sky-400 font-medium mb-1">Source URL</div>
                  <div className="text-gray-300 break-all">{image.source_url}</div>
                </div>
              )}
              {image.source_metadata?.caption && (
                <div>
                  <div className="text-sky-400 font-medium mb-1">Caption</div>
                  <div className="text-gray-300">{image.source_metadata.caption}</div>
                </div>
              )}
              {image.source_metadata?.title && (
                <div>
                  <div className="text-sky-400 font-medium mb-1">Title</div>
                  <div className="text-gray-300">{image.source_metadata.title}</div>
                </div>
              )}
              {image.ims_source_title && (
                <div>
                  <div className="text-sky-400 font-medium mb-1">Source Title</div>
                  <div className="text-gray-300">{image.ims_source_title}</div>
                </div>
              )}
              {image.ims_source_summary && (
                <div>
                  <div className="text-sky-400 font-medium mb-1">Source Summary</div>
                  <div className="text-gray-300">{image.ims_source_summary}</div>
                </div>
              )}
              {image.ims_source_content && (
                <div>
                  <div className="text-sky-400 font-medium mb-1">Source Content</div>
                  <div className="text-gray-300">{image.ims_source_content}</div>
                </div>
              )}
            </div>
            
            {image.entities && image.entities.length > 0 && (
              <div>
                <h4 className="text-sky-400 font-medium mb-3">Entities ({image.entities.length})</h4>
                <div className="space-y-3">
                  {image.entities.map((ent, index) => (
                    <div key={index} className="bg-slate-800 p-3 rounded-lg">
                      <h5 className="text-white font-medium mb-2">{ent.title}</h5>
                      <p className="text-gray-300 text-sm mb-2">{ent.description}</p>
                      <div className="text-xs text-gray-400">
                        <span className="bg-slate-700 px-2 py-1 rounded">ID: {ent.id}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {view === 'entity' && entity && (
          <div className="p-4">
            <div className="mb-4">
              <h4 className="text-sky-400 font-medium mb-2">Keyword</h4>
              <p className="text-white text-lg">{entity.keyword}</p>
            </div>

            <div className="mb-6">
              <h4 className="text-sky-400 font-medium mb-3">Entities ({entity.entities.filter((ent, index, arr) => arr.findIndex(e => e.id === ent.id) === index).length})</h4>
              <div className="space-y-3">
                {entity.entities.filter((ent, index, arr) => arr.findIndex(e => e.id === ent.id) === index).map((ent, index) => (
                  <div key={ent.id} className="bg-slate-800 p-3 rounded-lg">
                    <h5 className="text-white font-medium mb-2">{ent.title}</h5>
                    <p className="text-gray-300 text-sm mb-2">{ent.description}</p>
                    <div className="text-xs text-gray-400">
                      <span className="bg-slate-700 px-2 py-1 rounded">ID: {ent.id}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {relatedImages.length > 0 && (
              <div>
                <h4 className="text-sky-400 font-medium mb-3">Related Grounding Images ({relatedImages.length})</h4>
                <div className="grid grid-cols-2 gap-2">
                  {relatedImages.map((img, index) => (
                    <div 
                      key={index}
                      className="cursor-pointer group"
                      onClick={() => openImageSidebar(img)}
                    >
                      <div className="rounded-lg overflow-hidden">
                        <img
                          src={img.src || img.url}
                          alt={img.caption || 'Related image'}
                          className="w-full h-24 object-cover group-hover:opacity-80 transition-opacity"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = IMAGE_FALLBACK;
                          }}
                        />
                      </div>
                      <p className="text-xs text-gray-400 mt-1 truncate">
                        {img.caption || 'No caption'}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// GroundingConsole component
const GroundingConsole = ({ isOpen, onClose, isCollapsed, onToggleCollapse, groundingImages, groundingText, openGroundingImageViewer, openImageSidebar, qaResponses }) => {
  if (!isOpen) return null; // Don't render if not open

  return (
    <div
      className={`fixed inset-x-0 bottom-0 bg-slate-900 border-t border-slate-700 shadow-xl transition-all duration-300 ease-in-out z-50
        ${isCollapsed ? 'h-12' : 'h-2/5'}`}
      style={{ 
        height: isCollapsed ? '3rem' : '40vh',
        transform: 'translateY(0)',
      }}
      onAnimationEnd={() => {
        // Scale the main content when console appears
        const mainContent = document.querySelector('main');
        if (mainContent) {
          mainContent.style.transform = 'scale(0.95)';
          mainContent.style.transformOrigin = 'bottom';
          mainContent.style.transition = 'transform 0.3s ease-in-out';
        }
      }}
    >
      {/* Console Header */}
      <div className="flex items-center justify-between px-4 border-b border-slate-700 bg-slate-800">
        <h3 className="text-xs font-medium text-white">Grounding Console</h3>
        <div className="flex items-center space-x-4">
          {/* Collapse/Expand Button */}
          <button
            onClick={onToggleCollapse}
            className="p-2 rounded-full hover:bg-slate-700 text-white transition-colors duration-200"
            aria-label={isCollapsed ? "Expand console" : "Collapse console"}
          >
            {isCollapsed ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          {/* Close Button */}
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-slate-700 text-white transition-colors duration-200"
            aria-label="Close console"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Console Content - Visible only when not collapsed */}
      {!isCollapsed && (
        <div className="flex flex-col h-full p-4 overflow-y-auto scrollbar-hide">
          {/* Image Grounding Section */}
          <div className="mb-4">
            <h4 className="text-xs mb-3 flex items-center text-sky-400">
              MMIR Image Observations
            </h4>
            <div className="flex flex-nowrap overflow-x-auto pb-4 -mb-4 scrollbar-hide space-x-1">
              {groundingImages && groundingImages.length > 0 ? (
                groundingImages.map((img, i) => (
                  <div 
                    key={`ground-img-${i}`} 
                    className="flex-none w-50 flex flex-col cursor-pointer"
                    onClick={() => openImageSidebar(qaResponses?.mmir_images_grounding?.[i])}
                  >
                    <div className="rounded-lg overflow-hidden shadow-sm h-24">
                      <img
                        src={img.src}
                        alt={img.caption}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.onerror = null; e.target.src = IMAGE_FALLBACK; }}
                      />
                    </div>
                    {/* Caption below the image */}
                    <div className="mt-1 text-xs text-gray-400">
                      <p className="truncate">{truncateText(img.caption || 'No caption available', 25)}</p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="flex-none w-full text-center py-4 text-gray-500 text-xs">
                  No grounding images available
                </div>
              )}
            </div>
          </div>

          {/* Text Grounding Section (Sources) */}
          <div>
            <h4 className="text-xs mb-3 text-sky-400">
              NGS Grounding
            </h4>
            
            <div className="flex flex-col space-y-4">
              {/* Combined Sources Section */}
              {groundingText && groundingText.length > 0 ? (
                <div className="flex flex-nowrap overflow-x-auto pb-4 scrollbar-hide space-x-2">
                  {groundingText.map((item, index) => (
                    <div 
                      key={`source-${index}`} 
                      className="flex-none w-80 p-3 rounded-lg shadow-sm flex flex-col justify-between bg-slate-800 border border-slate-700"
                    >
                      {/* Source (retrievalService) */}
                      <div className="bg-slate-700 text-slate-200 px-2 py-1 rounded-full text-xs mb-2 inline-block w-fit">
                        {item.retrievalService || "Source"}
                      </div>
                      
                      {/* Title */}
                      {item.title && (
                        <h5 className="font-semibold text-xs text-white mb-2">
                          {item.title}
                        </h5>
                      )}
                      
                      {/* Text content */}
                      <p className="text-xs text-gray-300 mb-2">
                        {item.text || "No text available"}
                      </p>
                      
                      {/* URL */}
                      {item.url && (
                        <div className="text-xs text-sky-400 truncate mt-auto">
                          {item.url}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex-none w-full text-center py-4 text-gray-500 text-xs">
                  No grounding text available
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper function to truncate text
const truncateText = (text, maxLength) => {
  if (text.length > maxLength) {
    return text.substring(0, maxLength) + '...';
  }
  return text;
};



// Annotate Modal Component  
const AnnotateModal = ({ show, onClose, data, setData, step, setStep, searchTerm, uploadedImageUrl, annotationUploader, qaResponses, visualQaHandler, imageSelected, textSelected }) => {
  const [annotationModelData, setAnnotationModelData] = useState(null);
  const [isLoadingAnnotationData, setIsLoadingAnnotationData] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const scrollContainerRef = useRef(null);
  const qaResponsesRef = useRef(qaResponses);
  
  // Reset annotation data when qaResponses actually change (new search)
  useEffect(() => {
    if (qaResponsesRef.current !== qaResponses) {
      setAnnotationModelData(null);
      qaResponsesRef.current = qaResponses;
    }
  }, [qaResponses]);
  
  useEffect(() => {
    if (show && !annotationModelData && visualQaHandler) {
      const loadAnnotationData = async () => {
        setIsLoadingAnnotationData(true);
        try {
          const grounding_images = qaResponses?.mmir_images_grounding || [];
          const modelData = await visualQaHandler.constructAnnotationModel(
            searchTerm,
            uploadedImageUrl,
            textSelected,
            imageSelected,
            true,
            qaResponses,
            grounding_images
          );
          setAnnotationModelData(modelData);
        } catch (error) {
          console.error('Error loading annotation data:', error);
          onClose('annotation_failed');
        } finally {
          setIsLoadingAnnotationData(false);
        }
      };
      loadAnnotationData();
    }
  }, [show, annotationModelData, visualQaHandler, searchTerm, uploadedImageUrl, textSelected, imageSelected, qaResponses, onClose]);
  
  console.log('AnnotateModal render:', { show, step, data });
  if (!show) return null;

  if (isLoadingAnnotationData) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 text-center">
          <div className="w-12 h-12 border-4 border-t-4 border-t-sky-800 border-sky-700 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-lg font-medium text-sky-800">Preparing your annotation exercise...</p>
        </div>
      </div>
    );
  }

  const totalSteps = 5;
  const answers = [
    annotationModelData?.text_grounding_answer || "No text grounding answer available",
    annotationModelData?.image_grounding_answer || "No image grounding answer available",
    annotationModelData?.parametric_answer || "No parametric answer available"
  ];

  const options = [
    { value: 'incorrect', label: 'Incorrect' },
    { value: 'partial', label: 'Partially correct' },
    { value: 'correct', label: 'Correct' }
  ];

  const images = (annotationModelData?.images || []).slice(0, 10).map((img, i) => ({
    id: i,
    src: img.displayUrl,
    metadata: {
      sourceUrl: img?.sourceUrl || 'N/A',
      caption: img?.caption || 'N/A',
      title: img?.title || 'N/A',
      sourceTitle: img?.sourceTitle || 'N/A',
      sourceSummary: img?.sourceSummary || 'N/A',
      sourceContent: img?.sourceContent || 'N/A'
    }
  }));

  const relevancyOptions = [
    { value: 'not_relevant', label: 'Not relevant', color: 'text-red-800' },
    { value: 'somewhat_relevant', label: 'Somewhat relevant', color: 'text-yellow-800' },
    { value: 'highly_relevant', label: 'Highly relevant', color: 'text-green-800' }
  ];

  const getStatusColor = (value) => {
    switch(value) {
      case 'highly_relevant':
      case 'highly_useful':
        return 'text-green-800';
      case 'somewhat_relevant':
      case 'somewhat_useful':
        return 'text-yellow-800';
      case 'not_relevant':
        return 'text-red-800';
      default:
        return 'text-gray-700';
    }
  };

  const handleRadioChange = (answerIndex, value) => {
    setData(prev => ({
      ...prev,
      [`answer_${answerIndex}`]: value
    }));
  };

  const handleImageRelevancyChange = (imageIndex, value) => {
    setData(prev => ({
      ...prev,
      [`image_${imageIndex}`]: value
    }));
  };

  const handleMetadataRelevancyChange = (imageIndex, value) => {
    setData(prev => ({
      ...prev,
      [`metadata_${imageIndex}`]: value
    }));
  };

  const isStep1Complete = answers.every((_, index) => data[`answer_${index}`]);
  const isStep2Complete = images.every((_, index) => data[`image_${index}`]) && data.overall_grounding_utility_image;
  
  const relevantImagesForUtility = images.filter((_, index) => 
    data[`image_${index}`] === 'somewhat_relevant' || data[`image_${index}`] === 'highly_relevant'
  );
  const isStep3Complete = relevantImagesForUtility.every((image) => {
    const originalIndex = images.findIndex(img => img.id === image.id);
    return data[`image_utility_${originalIndex}`];
  });
  
  const isStep4Complete = images.every((_, index) => data[`metadata_${index}`]) && data.overall_metadata_utility_image;
  
  const relevantMetadataForUtility = images.filter((_, index) => 
    data[`metadata_${index}`] === 'somewhat_relevant' || data[`metadata_${index}`] === 'highly_relevant'
  );
  const isStep5Complete = relevantMetadataForUtility.every((image) => {
    const originalIndex = images.findIndex(img => img.id === image.id);
    return data[`metadata_utility_${originalIndex}`];
  });

  const handleNext = () => {
    if (step === 1) {
      const allNotRelevant = images.every((_, index) => data[`image_${index}`] === 'not_relevant');
      if (allNotRelevant) {
        setStep(3); 
        setTimeout(() => {
          if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = 0;
          }
        }, 0);
        return;
      }
    }
    if (step === 3) {
      const allMetadataNotRelevant = images.every((_, index) => data[`metadata_${index}`] === 'not_relevant');
      if (allMetadataNotRelevant) {
        return; 
      }
    }
    if (step < totalSteps - 1) {
      setStep(step + 1);
      setTimeout(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = 0;
        }
      }, 0);
    }
  };

  const getStepTitle = () => {
    if (step === 0) return "Step 1: Answer Correctness";
    if (step === 1) return "Step 2: Image Evaluation";
    if (step === 2) return "Step 3: Granular Image Utility";
    if (step === 3) return "Step 4: Metadata Evaluation";
    if (step === 4) return "Step 5: Granular Metadata Utility";
    return `Step ${step + 1}`;
  };

  const renderStepContent = () => {
    if (step === 0) {
      return (
        <>
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-700 mb-2">Label the given answer texts as follows:</p>
            <ol className="text-sm text-gray-600 space-y-1">
              <li><strong>1. Incorrect</strong> – The answer is wrong, misleading, or does not address the question.</li>
              <li><strong>2. Partially correct</strong> – The answer includes some correct elements, but is incomplete or contains minor errors.</li>
              <li><strong>3. Correct</strong> – The answer matches the reference in meaning and covers all required information accurately.</li>
            </ol>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left p-4 font-medium text-gray-700">Answer</th>
                  <th className="text-center p-4 font-medium text-gray-700">Incorrect</th>
                  <th className="text-center p-4 font-medium text-gray-700">Partially correct</th>
                  <th className="text-center p-4 font-medium text-gray-700">Correct</th>
                </tr>
              </thead>
              <tbody>
                {answers.map((answer, index) => (
                  <tr key={index} className="border-b border-gray-100">
                    <td className="p-4 text-sm text-gray-800">{answer}</td>
                    {options.map((option) => (
                      <td key={option.value} className="p-4 text-center">
                        <input
                          type="radio"
                          name={`answer_${index}`}
                          value={option.value}
                          checked={data[`answer_${index}`] === option.value}
                          onChange={(e) => handleRadioChange(index, e.target.value)}
                          className="w-4 h-4 text-sky-600 focus:ring-sky-600"
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      );
    } else if (step === 1) {
      return (
        <>
          <div className="mb-8 p-4 bg-sky-50 rounded-lg">
              <p className="text-sm text-gray-700 font-medium mb-2">Following question is about your query / image:</p>
              {uploadedImageUrl && (
                <div className="mt-3">
                  <p className="text-sm text-gray-700 mb-2">{searchTerm}</p>
                  <img
                    src={uploadedImageUrl}
                    alt="User provided image"
                    className="w-24 h-18 object-cover rounded"
                  />
                </div>
              )}
          </div>
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Image Relevancy</h3>
            <p className="text-sm text-gray-600 mb-2">How relevant is the retrieved image to the input image, based on key entities mentioned in the user query.</p>
            <ol className="text-sm text-gray-600 space-y-1">
              <li><strong>1. Not relevant</strong> – The retrieved image does not share any meaningful entities with the input image.</li>
              <li><strong>2. Somewhat relevant</strong> – The retrieved image includes entities that match the macro-category of those in the input image, but details differ (e.g., both images show a washing machine, but different models).</li>
              <li><strong>3. Highly relevant</strong> – The retrieved image includes exactly the same key entities as the input image, with matching granularity (e.g., same object and model).</li>
            </ol>
          </div>
          
          <div className="max-h-96 overflow-y-auto mb-6">
            <div className="space-y-4">
              {images.map((image, index) => (
                <div key={image.id} className="flex items-center py-3">
                  <img
                    src={image.src}
                    alt={`Image ${index + 1}`}
                    className="w-20 h-15 object-cover rounded mr-4 flex-shrink-0"
                  />
                  <div className="flex space-x-6">
                    {relevancyOptions.map((option) => (
                      <label key={option.value} className="flex items-center">
                        <input
                          type="radio"
                          name={`image_${index}`}
                          value={option.value}
                          checked={data[`image_${index}`] === option.value}
                          onChange={(e) => handleImageRelevancyChange(index, e.target.value)}
                          className="w-4 h-4 text-sky-600 focus:ring-sky-600 mr-2"
                        />
                        <span className={`text-sm ${option.color}`}>{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <hr className="border-gray-300 mb-6" />
          
          <div className="mb-6 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Overall Grounding Utility of Retrieved Image</h3>
            <div className="mb-2 p-4 bg-sky-50 rounded-lg">
                <p className="text-sm text-gray-700 mb-2 font-medium">Following question is about the given answer:</p>
                <p className="text-sm text-gray-700 mb-2"><i>{answers[1]}</i></p>
            </div>
            <p className="text-sm text-gray-600 mb-2">Does any part of the retrieved image appear in the generated answer by the model?</p>
            <ol className="text-sm text-gray-600 space-y-1">
              <li><strong>1. Yes</strong> – Some data from the retrieved multimodal response is used in the generated answer.</li>
              <li><strong>2. No</strong> – The retrieved data does not appear or contribute to the generated answer.</li>
            </ol>
          </div>
          
          <div className="flex space-x-6">
            <label className="flex items-center">
              <input
                type="radio"
                name="overall_grounding_utility_image"
                value="yes"
                checked={data.overall_grounding_utility_image === 'yes'}
                onChange={(e) => {
                  const value = e.target.value;
                  setData(prev => ({ ...prev, overall_grounding_utility_image: value }));
                }}
                className="w-4 h-4 text-sky-600 focus:ring-sky-600 mr-2"
              />
              <span className="text-sm text-gray-700">Yes</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="overall_grounding_utility_image"
                value="no"
                checked={data.overall_grounding_utility_image === 'no'}
                onChange={(e) => {
                  const value = e.target.value;
                  setData(prev => ({ ...prev, overall_grounding_utility_image: value }));
                }}
                className="w-4 h-4 text-sky-600 focus:ring-sky-600 mr-2"
              />
              <span className="text-sm text-gray-700">No</span>
            </label>
          </div>
        </>
      );
    } else if (step === 2) {
      // Filter images to show only those marked as somewhat or highly relevant
      const relevantImages = images.filter((_, index) => 
        data[`image_${index}`] === 'somewhat_relevant' || data[`image_${index}`] === 'highly_relevant'
      );
      
      return (
        <>
          <div className="mb-8 p-4 bg-sky-50 rounded-lg">
              <p className="text-sm text-gray-700 font-medium mb-2">Following question is about your query / image:</p>
              {uploadedImageUrl && (
                <div className="mt-3">
                  <p className="text-sm text-gray-700 mb-2">{searchTerm}</p>
                  <img
                    src={uploadedImageUrl}
                    alt="User provided image"
                    className="w-24 h-18 object-cover rounded"
                  />
                </div>
              )}
          </div>
          <div className="mb-6 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Granular Image Utility</h3>
            <p className="text-sm text-gray-600 mb-2">How useful is each retrieved image for answering the user query.</p>
            <ol className="text-sm text-gray-600 space-y-1">
              <li><strong>1. Somewhat useful</strong> – The image includes helpful visual information, but not enough to fully answer the query.</li>
              <li><strong>2. Highly useful</strong> – The image alone provides all necessary visual information to answer the query.</li>
            </ol>
          </div>
          
          <div className="max-h-96 overflow-y-auto mb-6">
            <div className="space-y-4">
              {relevantImages.map((image, displayIndex) => {
                const originalIndex = images.findIndex(img => img.id === image.id);
                return (
                  <div key={image.id} className="flex items-center py-3">
                    <img
                      src={image.src}
                      alt={`Image ${originalIndex + 1}`}
                      className="w-20 h-15 object-cover rounded mr-4 flex-shrink-0"
                    />
                    <div className="flex space-x-6">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name={`image_utility_${originalIndex}`}
                          value="somewhat_useful"
                          checked={data[`image_utility_${originalIndex}`] === 'somewhat_useful'}
                          onChange={(e) => {
                            const value = e.target.value;
                            setData(prev => ({ ...prev, [`image_utility_${originalIndex}`]: value }));
                          }}
                          className="w-4 h-4 text-sky-600 focus:ring-sky-600 mr-2"
                        />
                        <span className={`text-sm ${getStatusColor('somewhat_useful')}`}>Somewhat useful</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name={`image_utility_${originalIndex}`}
                          value="highly_useful"
                          checked={data[`image_utility_${originalIndex}`] === 'highly_useful'}
                          onChange={(e) => {
                            const value = e.target.value;
                            setData(prev => ({ ...prev, [`image_utility_${originalIndex}`]: value }));
                          }}
                          className="w-4 h-4 text-sky-600 focus:ring-sky-600 mr-2"
                        />
                        <span className={`text-sm ${getStatusColor('highly_useful')}`}>Highly useful</span>
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      );
    } else if (step === 3) {
      return (
        <>
          <div className="mb-8 p-4 bg-sky-50 rounded-lg">
              <p className="text-sm text-gray-700 font-medium mb-2">Following question is about your query / image:</p>
              {uploadedImageUrl && (
                <div className="mt-3">
                  <p className="text-sm text-gray-700 mb-2">{searchTerm}</p>
                  <img
                    src={uploadedImageUrl}
                    alt="User provided image"
                    className="w-24 h-18 object-cover rounded"
                  />
                </div>
              )}
          </div>
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Image Metadata Relevancy</h3>
            <p className="text-sm text-gray-600 mb-2">How relevant is the retrieved image meta-data to the user query, based on key entities.</p>
            <ol className="text-sm text-gray-600 space-y-1">
              <li><strong>1. Not relevant</strong> – The retrieved image meta-data is unrelated to the user query.</li>
              <li><strong>2. Somewhat relevant</strong> – The image meta-data aligns with the macro-category of the entities in the query, but details differ or are incomplete.</li>
              <li><strong>3. Highly relevant</strong> – The image meta-data matches the key entities in the query at a fine-grained level.</li>
            </ol>
          </div>
          
          <div className="max-h-96 overflow-y-auto mb-6">
            <div className="space-y-4">
              {images.map((image, index) => (
                <div key={image.id} className="py-3 border-b border-gray-100">
                  <div className="mb-3">
                    <div className="text-xs text-gray-500 space-y-1">
                      <div><strong>Source URL:</strong> {image.metadata.sourceUrl|| 'N/A'}</div>
                      <div><strong>Caption:</strong> {image.metadata.caption || 'N/A'}</div>
                      <div><strong>Title:</strong> {image.metadata.title || 'N/A'}</div>
                      <div><strong>Source Title:</strong> {image.metadata.sourceTitle || 'N/A'}</div>
                      <div><strong>Source Summary:</strong> {image.metadata.sourceSummary || 'N/A'}</div>
                      <div><strong>Source Content:</strong> {image.metadata.sourceContent || 'N/A'}</div>
                    </div>
                  </div>
                  <div className="flex space-x-6">
                    {relevancyOptions.map((option) => (
                      <label key={option.value} className="flex items-center">
                        <input
                          type="radio"
                          name={`metadata_${index}`}
                          value={option.value}
                          checked={data[`metadata_${index}`] === option.value}
                          onChange={(e) => handleMetadataRelevancyChange(index, e.target.value)}
                          className="w-4 h-4 text-sky-600 focus:ring-sky-600 mr-2"
                        />
                        <span className={`text-sm ${option.color}`}>{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <hr className="border-gray-300 mb-6" />
          
          <div className="mb-6 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Overall Grounding Utility of Metadata</h3>
            <div className="mb-2 p-4 bg-sky-50 rounded-lg">
                <p className="text-sm text-gray-700 mb-2 font-medium">Following question is about the given answer:</p>
                <p className="text-sm text-gray-700 mb-2"><i>{answers[1]}</i></p>
            </div>
            <p className="text-sm text-gray-600 mb-2">Does any part of the metadata appear in the generated answer by the model?</p>
            <ol className="text-sm text-gray-600 space-y-1">
              <li><strong>1. Yes</strong> – Some data from the retrieved multimodal response is used in the generated answer.</li>
              <li><strong>2. No</strong> – The retrieved data does not appear or contribute to the generated answer.</li>
            </ol>
          </div>
          
          <div className="flex space-x-6">
            <label className="flex items-center">
              <input
                type="radio"
                name="overall_metadata_utility_image"
                value="yes"
                checked={data.overall_metadata_utility_image === 'yes'}
                onChange={(e) => {
                  const value = e.target.value;
                  setData(prev => ({ ...prev, overall_metadata_utility_image: value }));
                }}
                className="w-4 h-4 text-sky-600 focus:ring-sky-600 mr-2"
              />
              <span className="text-sm text-gray-700">Yes</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="overall_metadata_utility_image"
                value="no"
                checked={data.overall_metadata_utility_image === 'no'}
                onChange={(e) => {
                  const value = e.target.value;
                  setData(prev => ({ ...prev, overall_metadata_utility_image: value }));
                }}
                className="w-4 h-4 text-sky-600 focus:ring-sky-600 mr-2"
              />
              <span className="text-sm text-gray-700">No</span>
            </label>
          </div>
        </>
      );
    } else if (step === 4) {
      // Filter images to show only those with metadata marked as somewhat or highly relevant
      const relevantMetadata = images.filter((_, index) => 
        data[`metadata_${index}`] === 'somewhat_relevant' || data[`metadata_${index}`] === 'highly_relevant'
      );
      
      return (
        <>
          <div className="mb-8 p-4 bg-sky-50 rounded-lg">
              <p className="text-sm text-gray-700 font-medium mb-2">Following question is about your query / image:</p>
              {uploadedImageUrl && (
                <div className="mt-3">
                  <p className="text-sm text-gray-700 mb-2">{searchTerm}</p>
                  <img
                    src={uploadedImageUrl}
                    alt="User provided image"
                    className="w-24 h-18 object-cover rounded"
                  />
                </div>
              )}
          </div>
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Granular Metadata Utility</h3>
            <p className="text-sm text-gray-600 mb-2">How useful is each retrieved metadata (text) for answering the user query</p>
            <ol className="text-sm text-gray-600 space-y-1">
              <li><strong>1. Somewhat useful</strong> – The text contains helpful information, but it's incomplete or insufficient for a full answer.</li>
              <li><strong>2. Highly useful</strong> – The text alone fully answers the query, with all required details.</li>
            </ol>
          </div>
          
          <div className="max-h-96 overflow-y-auto mb-6">
            <div className="space-y-4">
              {relevantMetadata.map((image, displayIndex) => {
                const originalIndex = images.findIndex(img => img.id === image.id);
                return (
                  <div key={image.id} className="py-3 border-b border-gray-100">
                    <div className="mb-3">
                      <div className="text-xs text-gray-500 space-y-1">
                        <div><strong>Source URL:</strong> {image.metadata.sourceUrl|| 'N/A'}</div>
                        <div><strong>Caption:</strong> {image.metadata.caption || 'N/A'}</div>
                        <div><strong>Title:</strong> {image.metadata.title || 'N/A'}</div>
                        <div><strong>Source Title:</strong> {image.metadata.sourceTitle || 'N/A'}</div>
                        <div><strong>Source Summary:</strong> {image.metadata.sourceSummary || 'N/A'}</div>
                        <div><strong>Source Content:</strong> {image.metadata.sourceContent || 'N/A'}</div>
                      </div>
                    </div>
                    <div className="flex space-x-6">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name={`metadata_utility_${originalIndex}`}
                          value="somewhat_useful"
                          checked={data[`metadata_utility_${originalIndex}`] === 'somewhat_useful'}
                          onChange={(e) => {
                            const value = e.target.value;
                            setData(prev => ({ ...prev, [`metadata_utility_${originalIndex}`]: value }));
                          }}
                          className="w-4 h-4 text-sky-600 focus:ring-sky-600 mr-2"
                        />
                        <span className={`text-sm ${getStatusColor('somewhat_useful')}`}>Somewhat useful</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name={`metadata_utility_${originalIndex}`}
                          value="highly_useful"
                          checked={data[`metadata_utility_${originalIndex}`] === 'highly_useful'}
                          onChange={(e) => {
                            const value = e.target.value;
                            setData(prev => ({ ...prev, [`metadata_utility_${originalIndex}`]: value }));
                          }}
                          className="w-4 h-4 text-sky-600 focus:ring-sky-600 mr-2"
                        />
                        <span className={`text-sm ${getStatusColor('highly_useful')}`}>Highly useful</span>
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      );
    } else {
      return (
        <div className="text-center py-12">
          <p className="text-gray-500">Step {step + 1} content coming soon...</p>
        </div>
      );
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto" ref={scrollContainerRef}>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">{getStepTitle()}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={20} />
          </button>
        </div>
        
        {/* Progress Bar */}
        <div className="mb-6">
          <div className="flex justify-between text-xs text-gray-500 mb-2">
            <span>Step {step + 1} of {totalSteps}</span>
            <span>{Math.round(((step + 1) / totalSteps) * 100)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-gradient-to-r from-sky-500 to-sky-700 h-2 rounded-full transition-all duration-300"
              style={{ width: `${((step + 1) / totalSteps) * 100}%` }}
            />
          </div>
        </div>
        
        {renderStepContent()}

        <div className="flex justify-end mt-6">
          {((step === 0 && isStep1Complete) || 
            (step === 1 && isStep2Complete) || 
            (step === 2 && isStep3Complete) || 
            (step === 3 && isStep4Complete) || 
            (step === 4 && isStep5Complete)) ? (
            (step < totalSteps - 1 && !(step === 3 && isStep4Complete && images.every((_, index) => data[`metadata_${index}`] === 'not_relevant'))) ? (
              <button
                onClick={handleNext}
                className="px-4 py-2 bg-sky-600 text-white rounded-md hover:bg-sky-700"
              >
                Next
              </button>
            ) : (
              <button
                onClick={async () => {
                  setIsSubmitting(true);
                  const finalData = { ...data };
                  const allImagesNotRelevant = images.every((_, index) => data[`image_${index}`] === 'not_relevant');
                  if (allImagesNotRelevant) {
                    images.forEach((_, index) => {
                      if (!finalData[`image_utility_${index}`]) {
                        finalData[`image_utility_${index}`] = "skipped because all images were marked irrelevant";
                      }
                    });
                  }
                  const allMetadataNotRelevant = images.every((_, index) => data[`metadata_${index}`] === 'not_relevant');
                  if (allMetadataNotRelevant) {
                    images.forEach((_, index) => {
                      if (!finalData[`metadata_utility_${index}`]) {
                        finalData[`metadata_utility_${index}`] = "skipped because all metadata were marked irrelevant";
                      }
                    });
                  }
                  
                  const dataWithQuery = {
                    ...finalData,
                    textQuery: searchTerm || '',
                    imageUrl: uploadedImageUrl || '',
                    answer_0_text: answers[0] || '',
                    answer_0_type: 'text_grounding_answer',
                    answer_1_text: answers[1] || '',
                    answer_1_type: 'image_grounding_answer', 
                    answer_2_text: answers[2] || '',
                    answer_2_type: 'parametric_answer'
                  };
                  console.log('Annotation Data:', dataWithQuery);
                  try {
                    const s3Key = await annotationUploader.uploadAnnotationToS3(dataWithQuery);
                    console.log('Annotation uploaded to S3:', s3Key);
                    onClose(true); 
                  } catch (error) {
                    console.error('Failed to upload annotation:', error);
                    onClose(false); 
                  } finally {
                    setIsSubmitting(false);
                  }
                }}
                className="px-4 py-2 bg-green-700 text-white rounded-md hover:bg-green-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-t-2 border-t-white border-gray-300 rounded-full animate-spin mr-2"></div>
                    Aggregating your annotations...
                  </>
                ) : (
                  'Done'
                )}
              </button>
            )
          ) : (
            <button
              className="px-4 py-2 bg-gray-400 text-white rounded-md cursor-not-allowed"
              disabled
            >
              Complete all answers
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Status Message Component
const StatusMessage = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor = type === 'success' ? 'bg-green-700' : 'bg-red-700';
  
  return (
    <div className={`fixed top-4 left-1/2 transform -translate-x-1/2 ${bgColor} text-white px-6 py-3 rounded-lg shadow-lg z-50 transition-all duration-300`}>
      {message}
    </div>
  );
};

// Main App component
const App = () => {
  let currentStage = getStage() || 'DEV';
  const [user, setUser] = useState(null);
  const [stage, setStage] = useState(currentStage);
  const [region, setRegion] = useState(getRegion(currentStage) || 'us-west-2');
  const [currentStep, setCurrentStep] = useState(0);
  const [showResults, setShowResults] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadingText, setLoadingText] = useState('');
  const [uploadedImageUrl, setUploadedImageUrl] = useState('');
  const [imageSelected, setImageSelected] = useState(false);
  const [textSelected, setTextSelected] = useState(false);
  const [queryIntelligenceSelected, setQueryIntelligenceSelected] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [persistedAnswer, setPersistedAnswer] = useState('');
  const [hasTyped, setHasTyped] = useState(false);
  const [typingProgress, setTypingProgress] = useState(0);
  const [hasSearched, setHasSearched] = useState(false);
  const [hasSearchedWithoutImage, setHasSearchedWithoutImage] = useState(false);
  const [videoFilters, setVideoFilters] = useState({ profile: DEFAULT_VIDEO_PROFILE });

  const [statusMessage, setStatusMessage] = useState(null);
  
  // URL params state
  const [searchTermQueryParam, setSearchTermQueryParam] = useState('');
  const [useGroundingQueryParam, setUseGroundingQueryParam] = useState(true);
  const [currentTabQueryParam, setCurrentTabQueryParam] = useState('images');
  const [useQueryIntelligenceQueryParam, setUseQueryIntelligenceQueryParam] = useState(false);
  

  function createAmplifyListener() {
      Hub.listen('auth', ({ payload: { event, data } }) => {
        switch (event) {
          case 'signIn':
          case 'cognitoHostedUI':
            console.debug("In cognito hosted ui: ", data)
            setUser({ user: data });
            break;
          case 'signIn_failure':
          case 'cognitoHostedUI_failure':
            console.debug('Sign in failure', data);
            break;
          default:
            console.debug("Event didn't match any of the expected events: ", event);
        }
      });
    }

    async function doCognitoAuth() {
      //fetch the user credentials if midway has already authenticated the user.
      try {
        const user = await Auth.currentAuthenticatedUser();
        setUser({ user: user });
        console.debug('Authenticated user ', user.getUsername());
      } catch (error) {
        console.debug('Not signed in. Error: ', error);
        //if couldn't fetch credentials, sign in the user by federate:
        await Auth.federatedSignIn({ customProvider: "FederateOIDC" });
      }
    }

    // Parse URL parameters on component mount
    useEffect(() => {
      const urlParams = new URLSearchParams(window.location.search);
      const termParam = urlParams.get('q') || '';
      const imageUrlParam = urlParams.get('imageUrl') || '';
      const useGroundingParam = urlParams.get('enableGrounding') === 'false' ? false : true;
      const useQueryIntelligenceParam = urlParams.get('enableQueryIntelligence') === 'true' ? true : false;
      const currentTab = imageUrlParam ? 'vqa' : 'images';
      
      setSearchTermQueryParam(termParam);
      setUseGroundingQueryParam(useGroundingParam);
      setUseQueryIntelligenceQueryParam(useQueryIntelligenceParam);
      setCurrentTabQueryParam(currentTab);
      
      if (termParam) {
        setSearchTerm(termParam);
        setTextSelected(useGroundingParam);
        setQueryIntelligenceSelected(useQueryIntelligenceParam);
      }
      if (imageUrlParam) {
        setUploadedImageUrl(imageUrlParam);
        setImageSelected(true);
      }
    }, []);

    useEffect(() => {
      initializeAmplify(stage, region);
      createAmplifyListener();
      const doAuth = async () => {
        await doCognitoAuth();
      }
      doAuth();
    }, [stage, region]);

  // Effect to update isUploaded based on uploadedImageUrl
  const [isUploaded, setIsUploaded] = useState(false);
  useEffect(() => {
    setIsUploaded((uploadedImageUrl || '').trim() !== '');
    if (!(uploadedImageUrl || '').trim()) {
      setHasSearchedWithoutImage(false);
    }
  }, [uploadedImageUrl]);
  
  // Auto-search if URL params exist
  useEffect(() => {
    if (searchTermQueryParam && !hasSearched) {
      handleSearchSubmit(true);
    }
  }, [searchTermQueryParam, hasSearched]);

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [imageUrlInput, setImageUrlInput] = useState('');
  const [displayedImageUrl, setDisplayedImageUrl] = useState('');

  const [showEnlargeModal, setShowEnlargeModal] = useState(false);
  const [enlargedImageUrl, setEnlargedImageUrl] = useState('');

  const [visualQaHandler, setVisualQaHandler] = useState(null);
  const [annotationUploader, setAnnotationUploader] = useState(null);
  const [imageUploader, setImageUploader] = useState(null);
  const [qaResponses, setQaResponses] = useState({});
  const [showAnnotateModal, setShowAnnotateModal] = useState(false);
  const [annotateData, setAnnotateData] = useState({});
  const [annotateStep, setAnnotateStep] = useState(0);

  useEffect(() => {
    if (visualQaExperimentalApiHost && visualQaExperimentalApiHost[stage] && visualQaExperimentalApiHost[stage][region]) {
      setVisualQaHandler(new VisualQaHandler(visualQaExperimentalApiHost[stage][region]));
    }
    if (visualQaDataUploadApiHost && visualQaDataUploadApiHost[stage] && visualQaDataUploadApiHost[stage][region]) {
      setAnnotationUploader(new AnnotationUploader(visualQaDataUploadApiHost[stage][region]));
      setImageUploader(new ImageUploader(visualQaDataUploadApiHost[stage][region]));
    }
  }, [stage, region]);

  const updateURLParams = (query, useGrounding = false, currentTab = 'images', useQueryIntelligence = false, imageUrl = '') => {
    const urlParams = new URLSearchParams();
    urlParams.set('enableGrounding', useGrounding);
    urlParams.set('enableQueryIntelligence', useQueryIntelligence);
    urlParams.set('tab', currentTab);
    if (query) urlParams.set('q', query);
    if (imageUrl) urlParams.set('imageUrl', imageUrl);
    const newUrl = window.location.pathname + (urlParams.toString() ? `?${urlParams.toString()}` : '');
    window.history.pushState({}, '', newUrl);
  };

  const handleSearchSubmit = async (fromInitialScreen = false) => {
    if ((searchTerm || '').trim() === '' && (uploadedImageUrl || '').trim() === '') {
      setErrorMessage('Please enter a query or upload a file.');
      return;
    }
    setErrorMessage('');
    const activeTab = uploadedImageUrl ? 'vqa' : 'images';
    updateURLParams(searchTerm, textSelected, activeTab, queryIntelligenceSelected, uploadedImageUrl);
    // Reset annotation exercise for new search
    setAnnotateData({});
    setAnnotateStep(0);
    // Reset persisted answer state for new search
    setPersistedAnswer('');
    setHasTyped(false);
    setTypingProgress(0);
    // Clear previous responses
    setQaResponses({});
    setIsLoading(true);

    const loadingMessages = [
      "Loading..."
    ];
    let messageIndex = 0;
    setLoadingText(loadingMessages[messageIndex]);

    const intervalId = setInterval(() => {
      messageIndex = (messageIndex + 1) % loadingMessages.length;
      setLoadingText(loadingMessages[messageIndex]);
    }, 3000);

    try {
      let response;
      if (uploadedImageUrl) {
        // VQA mode - image provided, use VQA handler
        setHasSearchedWithoutImage(false);
        response = await visualQaHandler.getVisualQaExperimentalResponses(searchTerm, uploadedImageUrl, imageSelected, textSelected, true);
      } else {
        // Normal mode - no image, use standard fetch
        setHasSearchedWithoutImage(true);
        const { apiHost, groundingGenerationApiHost } = await import('./config/apiConfig');
        const serviceClient = new (await import('./clients/tracerServiceClient')).default(apiHost[stage][region]);
        const llmAnswerGenerationClient = new (await import('./helpers/llmAnswerGeneration')).default(groundingGenerationApiHost[stage][region]);
        
        let llmAnswer = '';
        if (textSelected) {
          setLoadingText('Grounding your query and generating answer using Nova');
          llmAnswer = await llmAnswerGenerationClient.getLLMAnswer(searchTerm);
        }
        
        const fetchImages = async (query_text, llmAnswer) => {
          try {
            setLoadingText(`Fetching images for ${searchTerm} ${llmAnswer ? 'using query and answer' : ''}`);
            let images_request_body;
            if (llmAnswer) {
              images_request_body = getDiscoverImagesV2RequestWithGroundingBody(query_text, llmAnswer, "100", queryIntelligenceSelected);
            } else {
              images_request_body = getDiscoverImagesV2RequestBody(query_text, "100", queryIntelligenceSelected);
            }
            
            let images_response = await serviceClient.discoverImagesV2(images_request_body);
            if (images_response.statusCode === 451) {
              setErrorMessage('Request rejected due to sensitive content policy violation. Please check input and retry!');
              throw new Error(images_response.message);
            }
            if (images_response.statusCode !== 200) {
              throw new Error(images_response.message);
            }
            
            // Extract subqueries if query intelligence was used
            let subqueries = [];
            if (queryIntelligenceSelected && images_response.query_intelligence && images_response.query_intelligence.subqueries) {
              subqueries = images_response.query_intelligence.subqueries;
              setQaResponses(prev => ({...prev, query_intelligence_subqueries: subqueries}));
            }
            
            return images_response.images || [];
          } catch (error) {
            setLoadingText('');
            console.log(error);
            return [];
          }
        };
        
        const fetchVideos = async (query_text, llmAnswer) => {
          try {
            setLoadingText(`Fetching videos for ${searchTerm} ${llmAnswer ? 'using query and answer' : ''}`);
            const profile = videoFilters.profile || DEFAULT_VIDEO_PROFILE;
            let videos_request_body = llmAnswer
              ? getDiscoverVideosRequestWithGroundingBody(query_text, llmAnswer, profile)
              : getDiscoverVideosRequestBody(query_text, profile);
            let videos_response = await serviceClient.discoverImagesV2(videos_request_body);
            if (videos_response.statusCode === 451) {
              setErrorMessage('Request rejected due to sensitive content policy violation. Please check input and retry!');
              throw new Error(videos_response.message);
            }
            if (videos_response.statusCode !== 200) {
              throw new Error(videos_response.message);
            }
            return videos_response.videos || [];
          } catch (error) {
            setLoadingText('');
            console.log(error);
            return [];
          }
        };

        const [imagesResult, videosResult] = await Promise.all([
          fetchImages(searchTerm, llmAnswer),
          fetchVideos(searchTerm, llmAnswer)
        ]);

        response = {
          mmlm_answer: llmAnswer,
          mmir_images_aug: imagesResult,
          mmir_videos_aug: videosResult
        };
      }
      
      // Clear the interval and update state immediately after receiving response
      clearInterval(intervalId);
      setIsLoading(false);
      setQaResponses(response);
      setHasSearched(true);
      
      if (fromInitialScreen) {
        setCurrentStep(1);
        setShowResults(true);
      } else {
        setShowResults(true);
      }
    } catch (error) {
      clearInterval(intervalId);
      setIsLoading(false);
      setErrorMessage('Error fetching responses: ' + (error?.message || 'Unknown error'));
    }
  };

  const openUploadModal = () => {
    setShowUploadModal(true);
    if (uploadedImageUrl) {
      setImageUrlInput(uploadedImageUrl);
      setDisplayedImageUrl(uploadedImageUrl);
    }
  };

  const closeUploadModal = () => {
    setShowUploadModal(false);
  };

  const openEnlargeModal = (url) => {
    setEnlargedImageUrl(url);
    setShowEnlargeModal(true);
  };

  const closeEnlargeModal = () => {
    setShowEnlargeModal(false);
    setEnlargedImageUrl('');
  };

  const openAnnotateModal = () => {
    console.log('Opening annotate modal');
    setShowAnnotateModal(true);
  };

  const closeAnnotateModal = (success = null) => {
    console.log('Closing annotate modal');
    setShowAnnotateModal(false);
    setAnnotateData({});
    setAnnotateStep(0);
    
    // Show status message based on success/failure
    if (success === true) {
      setStatusMessage({
        message: 'Your annotations were successfully received. Thank you for your time!',
        type: 'success'
      });
    } else if (success === false) {
      setStatusMessage({
        message: 'Something went wrong with your annotations. Try again',
        type: 'error'
      });
    } else if (success === 'annotation_failed') {
      setStatusMessage({
        message: 'Sorry, we failed to create an annotation exercise for you at this point. Refresh and try again.',
        type: 'error'
      });
    }
  };

  const handleSuggestionClick = (suggestionQuery) => {
    setSearchTerm(suggestionQuery);
  };

  return (
    <div className="font-sans antialiased text-graphite-gray bg-bone-white min-h-screen flex flex-col">
      {/* Status Message */}
      {statusMessage && (
        <StatusMessage 
          message={statusMessage.message}
          type={statusMessage.type}
          onClose={() => setStatusMessage(null)}
        />
      )}      
      <style>
        {`
          body {
            font-family: 'Space Grotesk', sans-serif;
            line-height: 1.6;
            overflow: hidden;
          }
          .scrollbar-hide::-webkit-scrollbar {
            display: none;
          }
          .scrollbar-hide {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          * {
            scrollbar-width: none;
          }
          *::-webkit-scrollbar {
            display: none;
          }
          .backdrop-blur-md {
            backdrop-filter: blur(10px);
            -webkit-backdrop-filter: blur(10px);
          }
        `}
      </style>
      {/* Left Navigation Icons */}
      <div className="fixed -left-3 top-0 w-[100px] h-full flex flex-col py-4 space-y-6 items-center">
        <div className="flex flex-col items-center">
          <a  href="/unifiedapp"><img src={isDarkMode ? "/logo_darkmode.png" : "/logo_lightmode.png"} alt="Logo" className="mb-20" /></a>
        </div>
        <div className="flex flex-col items-center">
          <a href="/unifiedapp" className={`p-2 rounded-lg transition-colors duration-200 mb-1 ${isDarkMode ? 'text-gray-300 hover:text-white bg-[#333333]' : 'text-gray-600 hover:text-gray-800 bg-[#e1e4dc]'}`}>
            <Home size={16} />
          </a>
          <span className={`text-xs text-center ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Home</span>
        </div>
        <div className="flex flex-col items-center">
          <a href="https://insights.ideas.agi.amazon.dev/multimodal-information-retrieval" target="_blank" rel="noopener noreferrer" className={`p-2 rounded-lg transition-colors duration-200 mb-1 ${isDarkMode ? 'text-gray-300 hover:text-white hover:bg-[#333333]' : 'text-gray-600 hover:text-gray-800 hover:bg-[#e1e4dc]'}`}>
            <BarChart size={16} />
          </a>
          <span className={`text-xs text-center ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Metrics</span>
        </div>
        <div className="flex flex-col items-center">
          <a href="/tracer" className={`p-2 rounded-lg transition-colors duration-200 mb-1 ${isDarkMode ? 'text-gray-300 hover:text-white hover:bg-[#333333]' : 'text-gray-600 hover:text-gray-800 hover:bg-[#e1e4dc]'}`}>
            <SquareChevronRight size={16} />
          </a>
          <span className={`text-xs text-center ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Developer</span>
        </div>
      </div>
      
      {isLoading ? (
        <div className={`flex flex-col items-center justify-center h-screen overflow-hidden ${isDarkMode ? 'bg-[#242424] text-[#fcfcf9]' : 'bg-[#f3f3ed] text-graphite-gray'}`}>
          <div className={`rounded-lg p-8 ml-[70px] mr-[10px] mt-[10px] mb-[10px] w-[calc(100%-90px)] flex flex-col items-center justify-center h-[calc(100%-30px)] relative ${isDarkMode ? 'bg-[#333333]' : 'bg-[#fcfcf9]'}`}>
            <LoadingSpinner loadingText={loadingText} isDarkMode={isDarkMode} />
          </div>
        </div>
      ) : currentStep === 0 ? (
        <InitialSearchScreen
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          errorMessage={errorMessage}
          handleSearchSubmit={handleSearchSubmit}
          openUploadModal={openUploadModal}
          uploadedImageUrl={uploadedImageUrl}
          imageSelected={imageSelected}
          setImageSelected={setImageSelected}
          textSelected={textSelected}
          setTextSelected={setTextSelected}
          queryIntelligenceSelected={queryIntelligenceSelected}
          setQueryIntelligenceSelected={setQueryIntelligenceSelected}
          handleSuggestionClick={handleSuggestionClick}
          isDarkMode={isDarkMode}
          setIsDarkMode={setIsDarkMode}
        />
      ) : (
        <MainInteractionScreen
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          errorMessage={errorMessage}
          handleSearchSubmit={handleSearchSubmit}
          openUploadModal={openUploadModal}
          uploadedImageUrl={uploadedImageUrl}
          setShowResults={setShowResults}
          qaResponses={qaResponses}
          setQaResponses={setQaResponses}
          imageSelected={imageSelected}
          setImageSelected={setImageSelected}
          textSelected={textSelected}
          setTextSelected={setTextSelected}
          queryIntelligenceSelected={queryIntelligenceSelected}
          setQueryIntelligenceSelected={setQueryIntelligenceSelected}
          isDarkMode={isDarkMode}
          setIsDarkMode={setIsDarkMode}
          openAnnotateModal={openAnnotateModal}
          hasSearched={hasSearched}
          setHasSearched={setHasSearched}
          hasSearchedWithoutImage={hasSearchedWithoutImage}
          currentTabQueryParam={currentTabQueryParam}
          updateURLParams={updateURLParams}
          videoFilters={videoFilters}
          setVideoFilters={setVideoFilters}
        />
      )}
      <UploadModal
        show={showUploadModal}
        onClose={closeUploadModal}
        imageUrlInput={imageUrlInput}
        setImageUrlInput={setImageUrlInput}
        displayedImageUrl={displayedImageUrl}
        setDisplayedImageUrl={setDisplayedImageUrl}
        openEnlargeModal={openEnlargeModal}
        imageUploader={imageUploader}
        setUploadedImageUrl={setUploadedImageUrl}
        isDarkMode={isDarkMode}
      />
      <EnlargeImageModal
        show={showEnlargeModal}
        onClose={closeEnlargeModal}
        imageUrl={enlargedImageUrl}
      />
      <AnnotateModal
        show={showAnnotateModal}
        onClose={closeAnnotateModal}
        data={annotateData}
        setData={setAnnotateData}
        step={annotateStep}
        setStep={setAnnotateStep}
        searchTerm={searchTerm}
        uploadedImageUrl={uploadedImageUrl}
        annotationUploader={annotationUploader}
        qaResponses={qaResponses}
        visualQaHandler={visualQaHandler}
        imageSelected={imageSelected}
        textSelected={textSelected}
      />
    </div>
  );
};

export default App;
